import Konva from 'konva';
import type { Project } from '../core/model/project.js';
import { ComponentLibrary, makeComponentInstance } from '../core/library/loader.js';
import type { ComponentInstance } from '../core/model/component.js';
import type { ComponentDef } from '../core/library/component-def.js';
import type { BodyRotation, Uid } from '../core/model/types.js';
import { renderComponent } from './renderers/component-renderer.js';
import { computePinPath3D } from '../core/geometry/pin-path.js';
import { projectLocalToScene } from '../core/geometry/pin-path.js';
import { bend as bendOp, cut as cutOp, restore as restoreOp } from '../core/ops/pin-edit.js';
import type { BendDirection } from '../core/ops/pin-edit.js';
import type { Wire } from '../core/model/wire.js';
import { renderWire } from './renderers/wire-renderer.js';
import { wireAsPin } from '../core/geometry/wire-path.js';
import type { SolderAnchor, SolderAnchorAt } from '../core/model/solder-joint.js';
import { renderSolderBall } from './renderers/solder-renderer.js';

/**
 * Escena 2D del editor sobre Konva.
 * Coordenadas en mm, con escala global px/mm.
 * Interacciones: zoom, pan, selección jerárquica (componente / pin / punto
 * de doblez), arrastre de componentes y edición de dobleces / cortes.
 */

const BASE_PX_PER_MM = 10;
const MIN_SCALE = 1.5; // 15 % del zoom base
const MAX_SCALE = 100; // 1000 % del zoom base
const ZOOM_STEP = 1.2;
/** Pasos de deshacer/rehacer que se conservan. */
const HISTORY_LIMIT = 10;

/**
 * Selección jerárquica en la escena.
 * Cada nivel más específico incluye el componente al que pertenece.
 */
export type Selection =
  | null
  | { kind: 'component'; component_uid: Uid }
  | { kind: 'pin'; component_uid: Uid; pin_id: string }
  | { kind: 'bend'; component_uid: Uid; pin_id: string; bend_index: number }
  | { kind: 'multi'; component_uids: Uid[]; wire_uids?: Uid[] }
  | { kind: 'wire'; wire_uid: Uid }
  | { kind: 'wire-bend'; wire_uid: Uid; bend_index: number; side: 'left' | 'right' }
  | { kind: 'solder'; solder_uid: Uid };

export interface CanvasStageOptions {
  container: HTMLDivElement;
  library: ComponentLibrary;
  onSelectionChange?: (selection: Selection) => void;
  onOpenProperties?: (uid: Uid) => void;
  /**
   * Se llama cada vez que cambia el zoom (rueda, zoomIn/Out, fit,
   * carga inicial). El valor es el porcentaje relativo al zoom base
   * (100 = BASE_PX_PER_MM = 10 px/mm).
   */
  onZoomChange?: (percent: number) => void;
  /**
   * Se llama cuando comienza o termina la colocación de un cable
   * (fases 'floating' y 'growing'). Sirve a la toolbar para deshabilitar
   * botones y renombrar "Cable" → "Cancelar" durante la operación.
   */
  onPlacingWireChange?: (active: boolean) => void;
  /** Notifica inicio / fin del modo colocación de soldadura. */
  onPlacingSolderChange?: (active: boolean) => void;
  /**
   * URL del logo que se estampa en las imágenes exportadas. Si no se
   * indica se usa `logo.png` resuelto contra el documento. Si el
   * fichero no existe, la exportación cae al recuadro de marcador.
   */
  logoUrl?: string;
}

interface HitInfo {
  component_uid: Uid;
  kind: 'body' | 'pin' | 'bend' | 'wire' | 'wire-bend';
  pin_id?: string;
  bend_index?: number;
  wire_uid?: Uid;
}

export class CanvasStage {
  private readonly container: HTMLDivElement;
  private readonly stage: Konva.Stage;
  private readonly worldLayer: Konva.Layer;
  private readonly overlayLayer: Konva.Layer;
  private readonly componentsGroup: Konva.Group;
  private readonly wiresGroup: Konva.Group;
  private readonly soldersGroup: Konva.Group;
  private readonly resizeObserver: ResizeObserver;
  private readonly library: ComponentLibrary;
  private readonly onSelectionChange: ((selection: Selection) => void) | undefined;
  private readonly onOpenProperties: ((uid: Uid) => void) | undefined;
  private readonly onZoomChange: ((percent: number) => void) | undefined;
  private readonly onPlacingWireChange: ((active: boolean) => void) | undefined;
  private readonly onPlacingSolderChange: ((active: boolean) => void) | undefined;
  /** URL del logo a estampar en las exportaciones. */
  private readonly logoUrl: string;
  /** Logo ya cargado, o null si aún no se intentó / falló la carga. */
  private logoImage: HTMLImageElement | null = null;
  /** Carga en curso (se reutiliza para no pedir el fichero dos veces). */
  private logoLoad: Promise<HTMLImageElement | null> | null = null;

  private currentProject: Project | null = null;
  private selection: Selection = null;
  private spaceDown = false;
  private panning = false;
  private lastPanPoint: { x: number; y: number } | null = null;
  private dragging: {
    kind: 'component' | 'wire';
    uid: Uid;
    startWorld: { x: number; y: number };
    startPos: { x: number; y: number };
    /** Otros componentes que se arrastran a la vez (offset respecto al líder al inicio). */
    followers?: { uid: Uid; startPos: { x: number; y: number } }[];
    /** Cables que se arrastran a la vez (offset de wire.from). */
    wireFollowers?: { uid: Uid; startPos: { x: number; y: number } }[];
  } | null = null;
  /**
   * Rubber band en curso: se inicia al hacer mousedown en zona vacía y
   * dibuja un rectángulo de selección que crece con el ratón. Al soltar:
   *   - dirección izq → dcha: selecciona todo lo que TOQUE (intersección)
   *   - dirección dcha → izq: selecciona sólo lo que quede TOTALMENTE
   *     CONTENIDO dentro del rectángulo
   * Con Shift/Ctrl/Cmd se AÑADE al set previo en vez de reemplazar.
   */
  private rubberBand: {
    startWorld: { x: number; y: number };
    /** Última posición del cursor, usada para decidir la dirección. */
    lastWorld: { x: number; y: number };
    additive: boolean;
    prevSelection: Selection;
    moved: boolean;
    rect: Konva.Rect;
  } | null = null;
  private placing: {
    uid: Uid;
    /** UIDs adicionales que siguen al líder (para duplicado en bloque). */
    followUids?: Uid[];
    /** Offset de cada follower respecto al líder (en mm, en el instante inicial). */
    followOffsets?: { x: number; y: number }[];
    /** Cables duplicados que siguen al líder (offset de wire.from). */
    followWireUids?: Uid[];
    followWireOffsets?: { x: number; y: number }[];
  } | null = null;
  /**
   * Colocación de cable: flujo en 2 fases.
   *   phase 'floating': ambos extremos siguen al puntero como bloque
   *     (longitud fija de un paso), primer click fija `from` y pasa
   *     a la fase siguiente.
   *   phase 'growing':  `from` fijado; el cursor arrastra `to`
   *     alineado en recto (axial o diagonal) al paso de rejilla más
   *     cercano. Segundo click fija `to` y termina.
   */
  private placingWire: {
    uid: Uid;
    phase: 'floating' | 'growing';
    /** Dirección inicial (grados, convención Konva Y-down). Fijo por ahora. */
    initialDirDeg: number;
  } | null = null;
  /**
   * Modo colocación de soldadura activo: el siguiente click en un pin
   * o cable coloca una bola de soldadura en el vértice más cercano
   * (extremo o punto de doblez) del path clicado.
   */
  private placingSolder = false;
  /**
   * Redimensionado de un cable por uno de sus extremos: `which` indica
   * qué extremo agarra el usuario. Cambia el número de segmentos del
   * cable (bend_points_count) en pasos enteros de rejilla, manteniendo
   * fijo el OTRO extremo. Sólo se activa si el extremo agarrado y su
   * segmento contiguo pueden acortarse/alargarse sin re-interpretar el
   * doblez existente (bend contiguo con ángulo 0).
   */
  private resizingWire: {
    uid: Uid;
    which: 'from' | 'tip';
    /** Punto que queda FIJO durante el redimensionado (en world-mm). */
    fixed: { x: number; y: number };
    /** Dirección local del segmento redimensionado (unit vector). */
    dir: { x: number; y: number };
    /** Longitud de un paso a lo largo de esa dirección (axial o diagonal). */
    stepLen: number;
    /** bend_points_count al empezar. */
    initialCount: number;
    /** Índice del bend contiguo que se irá poblando/despoblando (o -1). */
    trimIndex: number;
    /**
     * Cursor world al hacer mousedown. Sirve para calcular el cambio
     * de tamaño como DELTA de arrastre desde el click, no como
     * proyección absoluta desde el pivote; esa proyección absoluta era
     * el bug que colapsaba el cable al primer mousemove si el pivote
     * quedaba lejos del extremo agarrado.
     */
    startWorld: { x: number; y: number };
    /** Copia del estado inicial para reconstruir el cable en cada mousemove. */
    initialBends: { angle_in_plane_deg: number; angle_out_of_plane_deg: number }[];
    initialFrom: { x: number; y: number };
    initialDir: number;
    /** Copia del estado en el frame previo, para saber si hay que rebuild. */
    lastCount: number;
  } | null = null;
  /**
   * Redimensionado del PIN de un componente. Mismo modelo que
   * `resizingWire` pero opera sobre `cut_at_index` en vez de sobre
   * `bend_points_count`. Los pines tienen longitud MÁXIMA (la
   * definida en `PinDef.bend_points_count + 1` segmentos) que no se
   * puede sobrepasar, y no se puede encoger a menos de 1 segmento ni
   * cruzar un bend con ángulo distinto de 0.
   */
  private resizingPin: {
    componentUid: Uid;
    pinIndex: number;
    /** Pivote en WORLD (después de aplicar transform de la instancia). */
    fixed: { x: number; y: number };
    /** Dirección de la cola en WORLD (unit vector). */
    dir: { x: number; y: number };
    /** Longitud de un segmento en WORLD. */
    stepLen: number;
    /** Cursor world al hacer mousedown (referencia para el delta). */
    startWorld: { x: number; y: number };
    /** Segmentos actuales en la cola (desde pivote hasta tip). */
    initialTail: number;
    /** Índice del pivote dentro del path (segments = pathLen - 1). */
    pivotVertexIdx: number;
    /** Número total máximo de segmentos = bend_points_count + 1. */
    maxTotalSegments: number;
    /** cut_at_index inmediato tras el último cambio, para dedup. */
    lastCut: number | null;
  } | null = null;
  /** Bola de vista previa que sigue al puntero durante el modo. */
  private solderPreview: Konva.Group | null = null;

  /**
   * Historial deshacer/rehacer. Se guardan instantáneas JSON del
   * proyecto completo (son datos planos y pequeños). Tope de
   * HISTORY_LIMIT pasos; al superarlo se descarta la más antigua.
   *
   * `pendingUndo` cubre las operaciones que abarcan varios eventos
   * (arrastrar, colocar, duplicar): se toma la foto al empezar y sólo
   * se confirma en el historial si la operación llega a modificar algo,
   * de modo que un click sin arrastre o una colocación cancelada no
   * gastan un paso.
   */
  private readonly undoStack: string[] = [];
  private readonly redoStack: string[] = [];
  private pendingUndo: string | null = null;
  private centeredOnce = false;
  // Snap SIEMPRE activo. La cuadrícula empieza visible; se puede ocultar.
  private gridVisible = true;
  private readonly gridLayer: Konva.Layer;
  private readonly gridStep: number = 2.54; // mm

  constructor(opts: CanvasStageOptions) {
    this.container = opts.container;
    this.library = opts.library;
    this.onSelectionChange = opts.onSelectionChange;
    this.onOpenProperties = opts.onOpenProperties;
    this.onZoomChange = opts.onZoomChange;
    this.onPlacingWireChange = opts.onPlacingWireChange;
    this.onPlacingSolderChange = opts.onPlacingSolderChange;
    this.logoUrl = opts.logoUrl ?? new URL('logo.png', document.baseURI).toString();

    const { width, height } = this.measure();

    this.stage = new Konva.Stage({
      container: this.container,
      width,
      height,
    });
    this.stage.scale({ x: BASE_PX_PER_MM, y: BASE_PX_PER_MM });
    this.stage.position({ x: width / 2, y: height / 2 });

    this.gridLayer = new Konva.Layer({ listening: false });
    this.worldLayer = new Konva.Layer({ listening: true });
    this.overlayLayer = new Konva.Layer({ listening: false });
    this.stage.add(this.gridLayer);
    this.stage.add(this.worldLayer);
    this.stage.add(this.overlayLayer);

    this.wiresGroup = new Konva.Group({ listening: true });
    this.worldLayer.add(this.wiresGroup);
    this.componentsGroup = new Konva.Group({ listening: true });
    this.worldLayer.add(this.componentsGroup);
    // Las bolas de soldadura van por encima de todo lo demás. Por
    // ahora no son interactivas (ni se seleccionan ni se arrastran),
    // así que se marcan non-listening para no robar clicks a los
    // pines/cables que hay debajo.
    this.soldersGroup = new Konva.Group({ listening: false });
    this.worldLayer.add(this.soldersGroup);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.container);

    this.stage.on('wheel', (e) => this.onWheel(e));
    // Konva NO sintetiza mousedown a partir de touchstart en móvil,
    // así que hay que registrar los dos alias en cada handler. El
    // KonvaEventObject<MouseEvent> también acepta TouchEvent en
    // runtime (el tipo es estático); el flujo de selección/arrastre
    // no lee campos exclusivos de MouseEvent (button se comprueba con
    // `?? 0` implícito porque el táctil siempre es "botón izquierdo").
    this.stage.on('mousedown touchstart', (e) => this.onMouseDown(e as Konva.KonvaEventObject<MouseEvent>));
    this.stage.on('mousemove touchmove', () => this.onMouseMove());
    this.stage.on('mouseup touchend', () => this.onMouseUp());
    this.stage.on('mouseleave touchcancel', () => this.onMouseUp());
    this.setupTouchGestures();
    this.stage.on('dblclick', (e) => this.onDoubleClick(e));

    this.container.tabIndex = 0;
    this.container.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        this.spaceDown = true;
        this.container.style.cursor = 'grab';
      } else if (e.code === 'Escape') {
        if (this.placingSolder) this.cancelSolderPlacement();
        else if (this.placingWire) this.cancelWirePlacement();
        else if (this.placing) this.cancelPlacement();
        else if (this.selection) this.setSelection(null);
      } else if (e.code === 'Delete' || e.code === 'Backspace') {
        // Suprimir: elimina componente/bloque seleccionado.
        if (this.selection) {
          e.preventDefault();
          this.deleteSelected();
        }
      }
    });
    this.container.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this.spaceDown = false;
        this.container.style.cursor = '';
      }
    });
    // Estado inicial del indicador de zoom.
    this.emitZoom();
  }

  private emitZoom(): void {
    if (!this.onZoomChange) return;
    const percent = Math.round((this.stage.scaleX() / BASE_PX_PER_MM) * 100);
    this.onZoomChange(percent);
  }

  destroy(): void {
    this.resizeObserver.disconnect();
    this.stage.destroy();
  }

  setProject(project: Project): void {
    this.currentProject = project;
    this.selection = null;
    // Cargar un esquema distinto invalida el historial anterior.
    this.undoStack.length = 0;
    this.redoStack.length = 0;
    this.pendingUndo = null;
    this.onSelectionChange?.(null);
    this.rebuild();
  }

  /** Proyecto actual (referencia viva, no clon). */
  getProject(): Project | null {
    return this.currentProject;
  }

  private rebuild(): void {
    if (!this.currentProject) {
      this.componentsGroup.destroyChildren();
      this.wiresGroup.destroyChildren();
      this.soldersGroup.destroyChildren();
      this.overlayLayer.destroyChildren();
      this.worldLayer.batchDraw();
      this.overlayLayer.batchDraw();
      return;
    }
    this.componentsGroup.destroyChildren();
    this.wiresGroup.destroyChildren();
    this.soldersGroup.destroyChildren();
    // Cables y componentes comparten `z_order` y se pintan ordenados
    // entre sí en un ÚNICO grupo. Antes cada uno vivía en su propio
    // grupo (wiresGroup / componentsGroup) y el orden de esos grupos
    // dictaba que los componentes siempre tapasen a los cables, así
    // que era imposible pasar un wire por encima de un componente ni
    // usar los botones ▲ Frente / ▼ Fondo sobre él. Mezclarlos
    // respeta el z_order tal cual, y el botón "Frente" sobre un wire
    // funciona igual que sobre un componente.
    const wires = this.currentProject.wires ?? [];
    const selectedWireSet = new Set<Uid>();
    if (this.selection?.kind === 'wire' || this.selection?.kind === 'wire-bend') {
      selectedWireSet.add(this.selection.wire_uid);
    } else if (this.selection?.kind === 'multi') {
      for (const wu of this.selection.wire_uids ?? []) selectedWireSet.add(wu);
    }
    const selectedUids = new Set(selectedComponentUids(this.selection));
    type WorldItem =
      | { kind: 'wire'; z: number; wire: Wire }
      | { kind: 'component'; z: number; inst: ComponentInstance; def: ComponentDef };
    const items: WorldItem[] = [];
    for (const w of wires) items.push({ kind: 'wire', z: w.z_order, wire: w });
    for (const inst of this.currentProject.components) {
      const def = this.library.get(inst.def_id);
      if (!def) continue;
      items.push({ kind: 'component', z: inst.z_order, inst, def });
    }
    items.sort((a, b) => a.z - b.z);
    for (const it of items) {
      if (it.kind === 'wire') {
        const node = renderWire(it.wire, true, selectedWireSet.has(it.wire.uid));
        // El cable que se está colocando se dibuja semitransparente
        // hasta que el usuario lo fija, para que se lea como "todavía
        // no está en el esquema".
        if (this.placingWire?.uid === it.wire.uid) node.opacity(0.45);
        this.componentsGroup.add(node);
      } else {
        // Marcas de doblez sólo cuando hay UN componente seleccionado
        // (en multi los pines no son editables individualmente).
        const showMarks = this.selection?.kind !== 'multi' && selectedUids.has(it.inst.uid);
        const node = renderComponent(it.inst, it.def, showMarks);
        this.componentsGroup.add(node);
      }
    }
    // Soldaduras: resuelven su ancla al world-position actual y se
    // pintan por encima de componentes y cables.
    for (const sj of this.currentProject.solder_joints ?? []) {
      const pos = this.resolveSolderAnchor(sj.anchor);
      if (!pos) continue;
      const rMm = sj.diameter_mm !== null && sj.diameter_mm !== undefined
        ? sj.diameter_mm / 2
        : this.defaultSolderRadius(sj.anchor);
      const g = renderSolderBall(sj.uid, rMm);
      g.position({ x: pos.x, y: pos.y });
      this.soldersGroup.add(g);
    }
    this.worldLayer.batchDraw();
    this.drawSelectionOverlay();
  }

  /**
   * Devuelve el world-position del vértice al que apunta el ancla, o
   * null si no se puede resolver (elemento borrado, índice fuera de
   * rango, etc.).
   */
  private resolveSolderAnchor(a: SolderAnchor): { x: number; y: number } | null {
    if (a.kind === 'pin') {
      const inst = this.findInstance(a.component_uid);
      const def = inst ? this.library.get(inst.def_id) : undefined;
      if (!inst || !def) return null;
      const pinIndex = def.pins.findIndex((p) => p.id === a.pin_id);
      if (pinIndex < 0) return null;
      const pinDef = def.pins[pinIndex]!;
      const pinState = inst.pins[pinIndex];
      if (!pinState) return null;
      const path = computePinPath3D(pinDef, pinState);
      const v = pickVertex(path, a.at);
      if (!v) return null;
      return projectLocalToScene({ ...v.position, z: 0 }, inst.position_mm, inst.rotation_deg, inst.flipped ?? false);
    }
    const wire = this.findWire(a.wire_uid);
    if (!wire) return null;
    const { def, state } = wireAsPin(wire);
    const path = computePinPath3D(def, state);
    const v = pickVertex(path, a.at);
    if (!v) return null;
    return { x: v.position.x, y: v.position.y };
  }

  /**
   * Diámetro por defecto de la bola de soldadura: 0.7 × paso de rejilla,
   * IGUAL para todas independientemente del ancho del pin o cable al que
   * se anclan. Antes escalaba con el grosor del elemento y las bolas
   * quedaban de tamaños dispares — se ve más limpio homogéneo.
   */
  private defaultSolderRadius(_a: SolderAnchor): number {
    // Ø = 0.56 · paso de rejilla (20 % menor que el 0.7 original).
    return (this.gridStep * 0.56) / 2;
  }

  zoomIn(): void {
    this.zoomBy(ZOOM_STEP, this.stageCenter());
  }

  zoomOut(): void {
    this.zoomBy(1 / ZOOM_STEP, this.stageCenter());
  }

  /**
   * Encuadra TODO el contenido lo más grande posible sin que se salga.
   * Toma la caja envolvente de `worldLayer` (componentes + cables +
   * soldaduras) en coordenadas de mundo, elige la escala que la hace
   * caber en el viewport dejando un margen, y centra.
   * Con el lienzo vacío vuelve al zoom base centrado en el origen.
   */
  fit(): void {
    const { width, height } = this.measure();
    const box = this.worldLayer.getClientRect({ relativeTo: this.worldLayer });
    const empty = !box || box.width <= 0 || box.height <= 0 ||
      !Number.isFinite(box.width) || !Number.isFinite(box.height);

    if (empty) {
      this.stage.scale({ x: BASE_PX_PER_MM, y: BASE_PX_PER_MM });
      this.stage.position({ x: width / 2, y: height / 2 });
    } else {
      const margin = 24; // px libres a cada lado
      const usableW = Math.max(1, width - margin * 2);
      const usableH = Math.max(1, height - margin * 2);
      const scale = clamp(
        Math.min(usableW / box.width, usableH / box.height),
        MIN_SCALE,
        MAX_SCALE,
      );
      this.stage.scale({ x: scale, y: scale });
      // Centrar la caja envolvente en el viewport.
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      this.stage.position({
        x: width / 2 - cx * scale,
        y: height / 2 - cy * scale,
      });
    }
    this.stage.batchDraw();
    this.drawSelectionOverlay();
    this.drawGrid();
    this.emitZoom();
  }

  // ---------- selección ----------

  setSelection(sel: Selection): void {
    if (selectionsEqual(this.selection, sel)) return;
    // Qué elementos se pintan distinto según la selección DENTRO del
    // worldLayer (no del overlay): las marcas de doblez de los pines
    // dependen del conjunto de COMPONENTES seleccionados, y las marcas
    // + los handles de extremo de los cables dependen del conjunto de
    // CABLES. Si cualquiera de los dos cambia hace falta `rebuild()`;
    // repintar sólo el overlay dejaría esos adornos pegados en pantalla.
    const prevComps = selectedComponentUids(this.selection);
    const nextComps = selectedComponentUids(sel);
    const prevWires = selectedWireUids(this.selection);
    const nextWires = selectedWireUids(sel);
    this.selection = sel;
    const worldLayerUnchanged =
      sameUidSet(prevComps, nextComps) && sameUidSet(prevWires, nextWires);
    if (!worldLayerUnchanged) {
      this.rebuild();
    } else {
      this.drawSelectionOverlay();
    }
    this.onSelectionChange?.(sel);
  }

  getSelection(): Selection {
    return this.selection;
  }

  // ---------- historial (deshacer / rehacer) ----------

  canUndo(): boolean { return this.undoStack.length > 0; }
  canRedo(): boolean { return this.redoStack.length > 0; }

  private snapshot(): string {
    return JSON.stringify(this.currentProject);
  }

  /** Registra el estado ACTUAL como punto al que puede volverse. */
  private pushHistory(): void {
    if (!this.currentProject) return;
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  /** Toma la foto de una operación que aún no ha modificado nada. */
  private beginPendingUndo(): void {
    this.pendingUndo = this.currentProject ? this.snapshot() : null;
  }

  /** Confirma la foto pendiente (la operación sí modificó algo). */
  private commitPendingUndo(): void {
    if (this.pendingUndo === null) return;
    this.undoStack.push(this.pendingUndo);
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack.length = 0;
    this.pendingUndo = null;
  }

  undo(): void {
    if (!this.currentProject || this.undoStack.length === 0) return;
    this.redoStack.push(this.snapshot());
    if (this.redoStack.length > HISTORY_LIMIT) this.redoStack.shift();
    this.restoreSnapshot(this.undoStack.pop()!);
  }

  redo(): void {
    if (!this.currentProject || this.redoStack.length === 0) return;
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.restoreSnapshot(this.redoStack.pop()!);
  }

  private restoreSnapshot(json: string): void {
    // Cualquier interacción en curso apunta a objetos del proyecto
    // anterior, así que se abandona antes de sustituirlo.
    this.placing = null;
    this.placingWire = null;
    this.dragging = null;
    this.pendingUndo = null;
    this.container.style.cursor = '';
    this.currentProject = JSON.parse(json) as Project;
    this.selection = null;
    this.onSelectionChange?.(null);
    this.rebuild();
  }

  // ---------- operaciones ----------

  toggleGrid(): boolean {
    this.gridVisible = !this.gridVisible;
    this.drawGrid();
    return this.gridVisible;
  }

  isGridVisible(): boolean {
    return this.gridVisible;
  }

  /**
   * Ajusta la posición de un componente para que el ATTACHMENT del
   * primer pin caiga en un nodo de la rejilla. Con esto (y dado que
   * `computePinPath3D` usa segmentos axiales/diagonales de longitud
   * grid-step / √2·grid-step), todos los vértices del pin — puntos
   * de doblez y tip — quedan también sobre la rejilla.
   */
  private snapToGridForComponent(
    instance: ComponentInstance,
    def: ComponentDef,
    proposedPos: { x: number; y: number },
  ): { x: number; y: number } {
    const pin = def.pins[0];
    if (!pin) return proposedPos;
    let localX = pin.attachment.x;
    let localY = instance.flipped ? -pin.attachment.y : pin.attachment.y;
    const rot = instance.rotation_deg * Math.PI / 180;
    const c = Math.cos(rot);
    const s = Math.sin(rot);
    const rotX = localX * c - localY * s;
    const rotY = localX * s + localY * c;
    const worldX = proposedPos.x + rotX;
    const worldY = proposedPos.y + rotY;
    const snappedX = Math.round(worldX / this.gridStep) * this.gridStep;
    const snappedY = Math.round(worldY / this.gridStep) * this.gridStep;
    return {
      x: snappedX - rotX,
      y: snappedY - rotY,
    };
  }

  private drawGrid(): void {
    this.gridLayer.destroyChildren();
    if (!this.gridVisible) {
      this.gridLayer.batchDraw();
      return;
    }
    // Dibuja puntos en cada intersección visible.
    const scale = this.stage.scaleX();
    const wPx = this.stage.width();
    const hPx = this.stage.height();
    const originPx = { x: this.stage.x(), y: this.stage.y() };
    // Rango de coordenadas mundo visibles.
    const xMinW = -originPx.x / scale;
    const xMaxW = (wPx - originPx.x) / scale;
    const yMinW = -originPx.y / scale;
    const yMaxW = (hPx - originPx.y) / scale;
    // Recorrido en pasos de gridStep.
    const startX = Math.floor(xMinW / this.gridStep) * this.gridStep;
    const startY = Math.floor(yMinW / this.gridStep) * this.gridStep;
    // Diámetro proporcional al zoom actual, en pixels de pantalla:
    //   - reduce al alejarse el zoom
    //   - desaparece si queda por debajo del umbral visible
    //   - se limita arriba para que no se convierta en manchas grandes
    const targetPx = Math.min(2.4, scale * 0.06);
    if (targetPx < 0.45) {
      this.gridLayer.batchDraw();
      return;
    }
    const dotR = targetPx / scale;
    for (let x = startX; x <= xMaxW; x += this.gridStep) {
      for (let y = startY; y <= yMaxW; y += this.gridStep) {
        this.gridLayer.add(new Konva.Circle({
          x, y, radius: dotR,
          // Tono cálido acorde con la paleta UI naranja/negro del logo.
          fill: '#c9915c', listening: false,
        }));
      }
    }
    this.gridLayer.batchDraw();
  }

  /** Cambia el `value` (part number / etiqueta) de la instancia y repinta. */
  setInstanceValue(uid: Uid, value: string): void {
    this.pushHistory();
    const inst = this.findInstance(uid);
    if (!inst) return;
    inst.value = value;
    this.rebuild();
  }

  private onDoubleClick(e: Konva.KonvaEventObject<MouseEvent>): void {
    if (this.placing) return;
    const hit = this.findHit(e.target);
    if (!hit) return;
    this.onOpenProperties?.(hit.component_uid);
  }

  /** Duplica el/los componente(s) seleccionado(s) y los ata al cursor.
   *  Clona posición, rotación, flip, estado de pines, value/label y
   *  properties. En multi-selección, el primer clon lidera el cursor
   *  y los demás mantienen su offset relativo al líder. */
  duplicateSelected(): void {
    const sel = this.selection;
    if (!sel || !this.currentProject) return;
    this.beginPendingUndo();
    const uids = selectedComponentUids(sel);
    const wireUids = selectedWireUids(sel);
    if (uids.length === 0 && wireUids.length === 0) return;
    const sources = uids.map((u) => this.findInstance(u)).filter((x): x is ComponentInstance => !!x);
    const wireSources = wireUids.map((u) => this.findWire(u)).filter((x): x is Wire => !!x);
    if (sources.length === 0 && wireSources.length === 0) return;
    this.cancelPlacement();

    // Referencia: usamos un componente como líder si existe; si no,
    // el primer cable (su `from`) hace de anchor.
    const leaderInst = sources[0];
    const leaderAnchor = leaderInst
      ? leaderInst.position_mm
      : wireSources[0]!.from;
    // Delta base: hacia el cursor (si está sobre el lienzo). Si el
    // cursor cae exactamente encima del líder original (o no hay
    // cursor), damos un offset de 1 paso de rejilla para que el
    // clon quede visualmente separado del original desde el primer
    // instante.
    const pointer = this.pointerWorld();
    let leaderDelta: { x: number; y: number };
    if (pointer) {
      leaderDelta = { x: pointer.x - leaderAnchor.x, y: pointer.y - leaderAnchor.y };
      const distSq = leaderDelta.x * leaderDelta.x + leaderDelta.y * leaderDelta.y;
      if (distSq < (this.gridStep * 0.5) ** 2) {
        leaderDelta = { x: this.gridStep, y: this.gridStep };
      }
    } else {
      leaderDelta = { x: this.gridStep, y: this.gridStep };
    }

    const maxZ = this.currentProject.components.reduce((m, c) => Math.max(m, c.z_order), 0);
    const maxWireZ = (this.currentProject.wires ?? []).reduce((m, w) => Math.max(m, w.z_order), 0);

    const cloneUids: Uid[] = [];
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i]!;
      const clone = cloneInstance(src, {
        x: src.position_mm.x + leaderDelta.x,
        y: src.position_mm.y + leaderDelta.y,
      }, maxZ + 1 + i);
      this.currentProject.components.push(clone);
      cloneUids.push(clone.uid);
    }

    // Clones de cables.
    const wireCloneUids: Uid[] = [];
    if (wireSources.length > 0) {
      if (!this.currentProject.wires) this.currentProject.wires = [];
      for (let i = 0; i < wireSources.length; i++) {
        const src = wireSources[i]!;
        const wc: Wire = {
          uid: `w${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}${i}`,
          from: { x: src.from.x + leaderDelta.x, y: src.from.y + leaderDelta.y },
          direction_deg: src.direction_deg,
          bend_points_count: src.bend_points_count,
          bends: src.bends.map((b) => ({ ...b })),
          cut_at_index: src.cut_at_index,
          z_order: maxWireZ + 1 + i,
        };
        this.currentProject.wires.push(wc);
        wireCloneUids.push(wc.uid);
      }
    }

    // Clones de SOLDADURAS. No forman parte de la selección (se anclan
    // a un pin o cable, no se seleccionan como bloque), así que se
    // arrastran implícitamente: se duplica toda soldadura cuyo ancla
    // apunte a un elemento que acabamos de clonar, remapeando el uid
    // al del clon. Sin esto, pegar un bloque perdía sus soldaduras.
    const compUidMap = new Map<Uid, Uid>();
    for (let i = 0; i < sources.length; i++) compUidMap.set(sources[i]!.uid, cloneUids[i]!);
    const wireUidMap = new Map<Uid, Uid>();
    for (let i = 0; i < wireSources.length; i++) wireUidMap.set(wireSources[i]!.uid, wireCloneUids[i]!);
    const solderCloneUids: Uid[] = [];
    // Snapshot: iteramos sobre una copia porque vamos añadiendo al array.
    const solderSources = (this.currentProject.solder_joints ?? []).slice();
    for (const sj of solderSources) {
      const anchor = remapSolderAnchor(sj.anchor, compUidMap, wireUidMap);
      if (!anchor) continue;
      if (!this.currentProject.solder_joints) this.currentProject.solder_joints = [];
      const uid = `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}${solderCloneUids.length}`;
      this.currentProject.solder_joints.push({
        uid,
        anchor,
        diameter_mm: sj.diameter_mm ?? null,
      });
      solderCloneUids.push(uid);
    }

    // Placing arrastra al líder. El líder es un componente si lo hay;
    // si sólo hay cables, promocionamos el primer cable como líder
    // "virtual" reutilizando el mecanismo de wire follow (ambos como
    // followWire con offset 0 y el resto con su offset).
    if (leaderInst) {
      const leaderUid = cloneUids[0]!;
      this.placing = {
        uid: leaderUid,
        followUids: cloneUids.slice(1),
        followOffsets: sources.slice(1).map((s) => ({
          x: s.position_mm.x - leaderInst.position_mm.x,
          y: s.position_mm.y - leaderInst.position_mm.y,
        })),
        followWireUids: wireCloneUids,
        followWireOffsets: wireSources.map((w) => ({
          x: w.from.x - leaderInst.position_mm.x,
          y: w.from.y - leaderInst.position_mm.y,
        })),
      };
    } else {
      // Sólo cables: no hay componente líder. En este caso los dejamos
      // fijos en el offset ya aplicado (sin seguir al cursor). El
      // usuario puede reposicionarlos con drag después.
      this.placing = null;
    }

    const totalComponents = cloneUids.length;
    const totalWires = wireCloneUids.length;
    if (totalComponents === 1 && totalWires === 0) {
      this.setSelection({ kind: 'component', component_uid: cloneUids[0]! });
    } else if (totalComponents === 0 && totalWires === 1) {
      this.setSelection({ kind: 'wire', wire_uid: wireCloneUids[0]! });
    } else {
      this.setSelection({
        kind: 'multi',
        component_uids: cloneUids,
        ...(totalWires > 0 ? { wire_uids: wireCloneUids } : {}),
      });
    }
    this.rebuild();
    this.container.style.cursor = leaderInst ? 'crosshair' : '';
  }

  /** Inicia el modo colocación: crea una instancia del componente y la ata
   *  al cursor hasta el próximo clic. Escape cancela y la elimina. */
  startPlacement(def: ComponentDef): void {
    if (!this.currentProject) return;
    this.cancelPlacement();
    this.beginPendingUndo();
    const world = this.pointerWorld() ?? { x: 0, y: 0 };
    const maxZ = this.currentProject.components.reduce((m, c) => Math.max(m, c.z_order), 0);
    const inst = makeComponentInstance(def, {
      position_mm: { x: world.x, y: world.y },
      z_order: maxZ + 1,
    });
    this.currentProject.components.push(inst);
    this.placing = { uid: inst.uid };
    this.setSelection({ kind: 'component', component_uid: inst.uid });
    this.rebuild();
    this.container.style.cursor = 'crosshair';
    // Enfocamos el lienzo para que los atajos (R, F, Space, flechas)
    // funcionen mientras el componente está pendiente de fijar.
    this.container.focus();
  }

  /** Cancela una colocación en curso (elimina la instancia pendiente). */
  cancelPlacement(): void {
    if (!this.placing || !this.currentProject) return;
    this.pendingUndo = null;
    // Se eliminan el líder Y los followers (componentes y cables)
    // porque todos formaban parte de la duplicación en curso.
    const uidsToRemove = new Set<Uid>([this.placing.uid, ...(this.placing.followUids ?? [])]);
    this.currentProject.components = this.currentProject.components.filter(
      (c) => !uidsToRemove.has(c.uid),
    );
    const wireUidsToRemove = new Set<Uid>(this.placing.followWireUids ?? []);
    if (wireUidsToRemove.size > 0 && this.currentProject.wires) {
      this.currentProject.wires = this.currentProject.wires.filter(
        (w) => !wireUidsToRemove.has(w.uid),
      );
    }
    // Las soldaduras clonadas junto al bloque quedarían colgando de
    // componentes/cables que acabamos de borrar: se eliminan también.
    if (this.currentProject.solder_joints) {
      this.currentProject.solder_joints = this.currentProject.solder_joints.filter((s) =>
        s.anchor.kind === 'pin'
          ? !uidsToRemove.has(s.anchor.component_uid)
          : !wireUidsToRemove.has(s.anchor.wire_uid),
      );
    }
    this.placing = null;
    this.setSelection(null);
    this.rebuild();
    this.container.style.cursor = '';
  }

  /** true si hay una colocación / duplicación en curso (componente). */
  isPlacing(): boolean {
    return this.placing !== null;
  }

  /**
   * Confirma la colocación en curso en la posición actual del líder
   * (equivalente al click que fija el componente / el bloque duplicado).
   * No hace nada si no hay placing activo.
   *
   * Si el placing venía de una duplicación en bloque (con followers)
   * se preserva la selección multi para que el usuario pueda seguir
   * operando sobre TODO el bloque duplicado.
   */
  confirmPlacement(): void {
    if (!this.placing) return;
    this.commitPendingUndo();
    const uid = this.placing.uid;
    const followUids = this.placing.followUids ?? [];
    const followWireUids = this.placing.followWireUids ?? [];
    this.placing = null;
    this.container.style.cursor = '';
    if (followUids.length === 0 && followWireUids.length === 0) {
      this.setSelection({ kind: 'component', component_uid: uid });
    } else {
      this.setSelection({
        kind: 'multi',
        component_uids: [uid, ...followUids],
        ...(followWireUids.length > 0 ? { wire_uids: followWireUids } : {}),
      });
    }
  }

  /**
   * Desplaza la selección actual en (dx, dy) pasos de rejilla.
   * Aplica a componentes (single, multi) y a cables. En multi-selección
   * con bend incluido, mueve todo el bloque (no dobla).
   */
  moveSelectedByGrid(dx: number, dy: number): void {
    this.pushHistory();
    this.moveSelectedByGridImpl(dx, dy);
    this.resyncInteractionBaselines();
  }

  private moveSelectedByGridImpl(dx: number, dy: number): void {
    if (!this.currentProject) return;
    const sel = this.selection;
    if (!sel) return;
    const step = this.gridStep;
    const shift = { x: dx * step, y: dy * step };

    if (sel.kind === 'wire' || sel.kind === 'wire-bend') {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      wire.from = { x: wire.from.x + shift.x, y: wire.from.y + shift.y };
      this.rebuild();
      return;
    }

    const compUids = selectedComponentUids(sel);
    const wireUids = selectedWireUids(sel);
    for (const uid of compUids) {
      const inst = this.findInstance(uid);
      if (!inst) continue;
      inst.position_mm = { x: inst.position_mm.x + shift.x, y: inst.position_mm.y + shift.y };
    }
    for (const wu of wireUids) {
      const wire = this.findWire(wu);
      if (!wire) continue;
      wire.from = { x: wire.from.x + shift.x, y: wire.from.y + shift.y };
    }
    if (compUids.length === 0 && wireUids.length === 0) return;
    this.rebuild();
  }

  /**
   * Serializa el lienzo actual como PNG (data URL).
   *
   * La imagen se genera SIEMPRE a zoom 100 % (BASE_PX_PER_MM) para no
   * perder resolución si el usuario tenía la vista alejada, y encuadra
   * la caja envolvente de TODO el contenido (componentes + cables +
   * soldaduras). El tamaño resultante es variable: crece con el
   * circuito.
   *
   * Durante la exportación se ocultan overlays de selección y las
   * marcas de puntos de doblez (tanto de pines como de cables), y se
   * puede pintar un fondo blanco opcional. Añade también un
   * marcador donde luego irá insertado un logo por PNG (por ahora un
   * recuadro con la palabra "LOGO").
   */
  /**
   * Precarga el logo que se estampa en las exportaciones. Devuelve la
   * imagen o null si el fichero no existe / no se pudo decodificar.
   * `toImageDataURL` es síncrono, así que conviene esperar a esta
   * promesa ANTES de exportar; si no, la primera exportación saldría
   * con el recuadro de marcador.
   */
  async ensureLogoLoaded(): Promise<HTMLImageElement | null> {
    if (this.logoImage) return this.logoImage;
    if (!this.logoLoad) {
      this.logoLoad = new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        // Permite que el canvas siga siendo exportable si el logo se
        // sirve desde otro origen con CORS habilitado.
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = this.logoUrl;
      }).then((img) => {
        this.logoImage = img;
        return img;
      });
    }
    return this.logoLoad;
  }

  toImageDataURL(opts: { background?: 'transparent' | 'white' } = {}): string {
    const background = opts.background ?? 'transparent';
    const overlayWasVisible = this.overlayLayer.visible();
    const gridWasVisible = this.gridLayer.visible();
    // Estado del stage.
    const prevScale = this.stage.scaleX();
    const prevPos = { x: this.stage.x(), y: this.stage.y() };
    const prevW = this.stage.width();
    const prevH = this.stage.height();
    // Marcas de doblez (pines + cables): apagadas para la exportación.
    const marks = this.collectBendMarkNodes();
    const marksPrevVisible = marks.map((m) => m.visible());
    // Nodos añadidos para la exportación (fondo, marco, logo, etc.)
    // pertenecientes al worldLayer — se destruyen tras exportar.
    const exportOnly: Konva.Node[] = [];

    try {
      // Apagamos overlays y marcas.
      this.overlayLayer.visible(false);
      this.gridLayer.visible(false);
      marks.forEach((m) => m.visible(false));

      // Caja envolvente del contenido en coords mundo.
      const box = this.worldLayer.getClientRect({ relativeTo: this.worldLayer });
      const empty = !box || box.width <= 0 || box.height <= 0
        || !Number.isFinite(box.width) || !Number.isFinite(box.height);

      // Margen alrededor del circuito, en mm (≈2 pasos de rejilla).
      const marginMm = 5.08;
      const gapMm = 2.54;
      const contentW = empty ? 20 : box.width;
      const contentH = empty ? 20 : box.height;
      const contentBox = empty
        ? { x: -contentW / 2, y: -contentH / 2, width: contentW, height: contentH }
        : box;

      // El recuadro reservado para el logo respeta SIEMPRE la
      // proporción real del PNG: si el logo es apaisado (4:1) el hueco
      // también lo es. Antes forzaba un alto mínimo, así que Konva
      // estiraba la imagen para llenar el rectángulo asignado y el
      // logo salía deformado (o, con recortes por resolución, parecía
      // cortado).
      const logo = this.logoImage;
      const logoAspect = logo && logo.naturalHeight > 0
        ? logo.naturalWidth / logo.naturalHeight
        : 2;

      // Búsqueda escalonada: probamos tamaños de logo de mayor a menor,
      // buscando en cada paso un hueco central que lo albergue. Nos
      // quedamos con el PRIMERO que quepa — así el logo aparece lo más
      // grande posible en un hueco vacío del esquema, sin superponerse
      // a componentes, cables o soldaduras.
      const preferredW = Math.min(60, Math.max(14, contentW * 0.5));
      const minW = 14;
      let logoW = preferredW;
      let logoH = logoW / logoAspect;
      let gap: { x: number; y: number } | null = null;
      while (logoW >= minW) {
        gap = this.findLogoGap(contentBox, logoW, logoH);
        if (gap) break;
        logoW *= 0.85;
        logoH = logoW / logoAspect;
      }

      let logoX: number;
      let logoY: number;
      let regionMinX: number;
      let regionMinY: number;
      let regionMaxX: number;
      let regionMaxY: number;
      if (gap) {
        logoX = gap.x;
        logoY = gap.y;
        // Región = contenido + margen; el logo cabe DENTRO del contenido.
        regionMinX = contentBox.x - marginMm;
        regionMinY = contentBox.y - marginMm;
        regionMaxX = contentBox.x + contentBox.width + marginMm;
        regionMaxY = contentBox.y + contentBox.height + marginMm;
      } else {
        // Fallback: región = contenido + margen + fila inferior para
        // el logo (esquina inferior derecha). En un esquema MUY
        // pequeño el ancho del contenido puede ser menor que el logo
        // preferido, así que:
        //  1) reducimos el logo al ancho disponible (contentW),
        //  2) si aun así el logo no cabe con margen, AMPLIAMOS la
        //     región horizontalmente hasta encajarlo.
        // Antes se restablecía `logoW = preferredW` sin más, y con un
        // esquema diminuto el logo se pintaba fuera del recuadro y
        // salía recortado en el PNG.
        logoW = Math.min(preferredW, Math.max(minW, contentBox.width));
        logoH = logoW / logoAspect;
        regionMinX = contentBox.x - marginMm;
        regionMinY = contentBox.y - marginMm;
        regionMaxX = contentBox.x + contentBox.width + marginMm;
        // Ancho MÍNIMO para que el logo entre con márgenes: si el
        // contenido no lo cubre, se ensancha la región centrando el
        // contenido a la izquierda.
        const minRegionW = logoW + marginMm * 2;
        if (regionMaxX - regionMinX < minRegionW) {
          regionMaxX = regionMinX + minRegionW;
        }
        regionMaxY = contentBox.y + contentBox.height + marginMm + logoH + gapMm;
        logoX = regionMaxX - marginMm - logoW;
        logoY = regionMaxY - marginMm - logoH;
      }
      const regionW = regionMaxX - regionMinX;
      const regionH = regionMaxY - regionMinY;

      // Fondo opcional (blanco) — Rect a nivel de mundo cubriendo la región.
      if (background === 'white') {
        const bg = new Konva.Rect({
          x: regionMinX,
          y: regionMinY,
          width: regionW,
          height: regionH,
          fill: '#ffffff',
          listening: false,
        });
        // Bajo TODO el contenido.
        this.worldLayer.add(bg);
        bg.moveToBottom();
        exportOnly.push(bg);
      }

      // Logo. Su posición se decidió antes: en un hueco central del
      // esquema si hay sitio, o en la esquina inferior derecha si no.
      // Si el PNG está cargado se estampa tal cual; si no existe, se
      // deja el recuadro punteado de marcador para que se vea dónde
      // iría.
      const logoGroup = new Konva.Group({ listening: false });
      if (logo) {
        logoGroup.add(new Konva.Image({
          image: logo,
          x: logoX,
          y: logoY,
          width: logoW,
          height: logoH,
          listening: false,
        }));
      } else {
        logoGroup.add(new Konva.Rect({
          x: logoX,
          y: logoY,
          width: logoW,
          height: logoH,
          stroke: '#555',
          strokeWidth: 0.2,
          dash: [1.0, 0.6],
          fill: background === 'white' ? '#fafafa' : 'rgba(255,255,255,0.85)',
          cornerRadius: 0.5,
          listening: false,
        }));
        const logoFont = Math.min(logoH * 0.35, logoW * 0.18);
        logoGroup.add(new Konva.Text({
          x: logoX,
          y: logoY,
          width: logoW,
          height: logoH,
          text: 'LOGO',
          fontSize: logoFont,
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          fontStyle: 'bold',
          fill: '#555',
          align: 'center',
          verticalAlign: 'middle',
          listening: false,
        }));
      }
      this.worldLayer.add(logoGroup);
      exportOnly.push(logoGroup);

      // Zoom 100 % (BASE_PX_PER_MM) y desplazamos el stage para que la
      // esquina superior izquierda de la región de exportación caiga
      // sobre (0,0) en coordenadas de stage. Así podemos capturar
      // directamente desde el origen del stage con toDataURL.
      const exportW = Math.ceil(regionW * BASE_PX_PER_MM);
      const exportH = Math.ceil(regionH * BASE_PX_PER_MM);
      this.stage.scale({ x: BASE_PX_PER_MM, y: BASE_PX_PER_MM });
      this.stage.position({
        x: -regionMinX * BASE_PX_PER_MM,
        y: -regionMinY * BASE_PX_PER_MM,
      });
      this.stage.width(exportW);
      this.stage.height(exportH);

      this.worldLayer.batchDraw();

      const pixelRatio = 2;
      return this.stage.toDataURL({
        pixelRatio,
        mimeType: 'image/png',
        x: 0,
        y: 0,
        width: exportW,
        height: exportH,
      });
    } finally {
      // Limpiamos nodos añadidos.
      for (const n of exportOnly) n.destroy();
      // Restauramos overlays / marcas.
      this.overlayLayer.visible(overlayWasVisible);
      this.gridLayer.visible(gridWasVisible);
      for (let i = 0; i < marks.length; i++) marks[i]!.visible(marksPrevVisible[i]!);
      // Restauramos escala, posición y tamaño lógico del stage.
      this.stage.scale({ x: prevScale, y: prevScale });
      this.stage.position(prevPos);
      this.stage.width(prevW);
      this.stage.height(prevH);
      this.stage.batchDraw();
    }
  }

  /**
   * Busca dentro del contentBox un hueco de `wMm × hMm` que no
   * colisione con NINGÚN componente, cable o soldadura. Prueba
   * posiciones candidatas en una rejilla gruesa recorrida desde el
   * centro hacia afuera (para preferir la zona central del esquema).
   * Devuelve la esquina superior-izquierda del hueco encontrado, o
   * null si no hay ninguno. Todas las coords en world-mm.
   */
  private findLogoGap(
    contentBox: { x: number; y: number; width: number; height: number },
    wMm: number,
    hMm: number,
  ): { x: number; y: number } | null {
    if (!this.currentProject) return null;
    // Margen que dejamos entre el logo y cualquier objeto.
    const pad = 1.5; // mm
    const bx0 = contentBox.x;
    const by0 = contentBox.y;
    const bx1 = contentBox.x + contentBox.width;
    const by1 = contentBox.y + contentBox.height;
    // Cajas ocupadas: componentes + cables + soldaduras.
    const obstacles: { x: number; y: number; width: number; height: number }[] = [];
    for (const inst of this.currentProject.components) {
      const node = this.findComponentNode(inst.uid);
      if (!node) continue;
      obstacles.push(node.getClientRect({ relativeTo: this.worldLayer }));
    }
    for (const w of this.currentProject.wires ?? []) {
      obstacles.push(wireBoundingBox(w));
    }
    for (const sj of this.currentProject.solder_joints ?? []) {
      const pos = this.resolveSolderAnchor(sj.anchor);
      if (!pos) continue;
      const r = sj.diameter_mm !== null && sj.diameter_mm !== undefined
        ? sj.diameter_mm / 2
        : this.defaultSolderRadius(sj.anchor);
      obstacles.push({ x: pos.x - r, y: pos.y - r, width: r * 2, height: r * 2 });
    }

    // Padding expandido: para test de colisión, inflamos los obstáculos.
    const inflated = obstacles.map((o) => ({
      x: o.x - pad, y: o.y - pad,
      width: o.width + pad * 2, height: o.height + pad * 2,
    }));

    // Rango donde puede caber el logo (su esquina sup-izq).
    const xMax = bx1 - wMm;
    const yMax = by1 - hMm;
    if (xMax < bx0 || yMax < by0) return null;

    // Muestreo: paso proporcional al tamaño del logo.
    const step = Math.max(this.gridStep, Math.min(wMm, hMm) / 4);
    // Puntos candidatos ordenados por cercanía al centro del contentBox.
    const cx = (bx0 + bx1) / 2 - wMm / 2;
    const cy = (by0 + by1) / 2 - hMm / 2;
    const candidates: { x: number; y: number; d2: number }[] = [];
    for (let y = by0; y <= yMax; y += step) {
      for (let x = bx0; x <= xMax; x += step) {
        candidates.push({ x, y, d2: (x - cx) ** 2 + (y - cy) ** 2 });
      }
    }
    candidates.sort((a, b) => a.d2 - b.d2);

    for (const c of candidates) {
      const test = { x: c.x, y: c.y, width: wMm, height: hMm };
      let clash = false;
      for (const o of inflated) {
        if (rectsIntersect(test, o)) { clash = true; break; }
      }
      if (!clash) return { x: c.x, y: c.y };
    }
    return null;
  }

  /**
   * Recolecta los grupos/nodos de marcas de doblez visibles en escena:
   *   - Marcas de pines de componentes: grupos con `pinId` que a su vez
   *     contienen ticks con `nodeKind: 'bend'`.
   *   - Marcas de puntos de doblez de cables: grupos con `wireUid` cuyo
   *     primer hijo es un tick `nodeKind: 'bend'`.
   * Devuelve los nodos padre para que se puedan ocultar/mostrar en bloque.
   */
  private collectBendMarkNodes(): Konva.Node[] {
    const out: Konva.Node[] = [];
    // Grupos de componentes: buscamos hijos que sean grupos de bends
    // (grupo cuyo primer hijo tenga nodeKind='bend').
    this.componentsGroup.find((n: Konva.Node) => {
      if (!(n instanceof Konva.Group)) return false;
      const first = n.getChildren()[0];
      if (first && first.getAttr('nodeKind') === 'bend') {
        out.push(n);
      }
      return false;
    });
    // Los cables se pintan ahora en el mismo grupo que los
    // componentes (para que compartan z_order), así que su búsqueda
    // ya está cubierta arriba: el subgrupo de marcas de doblez de un
    // cable tiene el mismo nodeKind='bend' que el de un pin.
    return out;
  }

  /**
   * Inicia la colocación de un CABLE. Aparece un cable de un paso
   * de rejilla atado al puntero (fase 'floating'). El siguiente
   * click fija el extremo `from` y comienza la fase 'growing',
   * en la que el cursor arrastra `to` alineado en recto al paso
   * de rejilla más cercano. Un segundo click confirma; Escape
   * cancela y elimina el cable pendiente.
   */
  startWirePlacement(): void {
    if (!this.currentProject) return;
    this.beginPendingUndo();
    this.cancelPlacement();
    this.cancelWirePlacement();
    if (!this.currentProject.wires) this.currentProject.wires = [];
    const world = this.pointerWorld() ?? { x: 0, y: 0 };
    const anchor = snapPointToGrid(world, this.gridStep);
    const initialDirDeg = 0; // hacia +X por ahora; a futuro seleccionable
    const maxZ = (this.currentProject.wires ?? []).reduce((m, w) => Math.max(m, w.z_order), 0);
    const wire: Wire = {
      uid: `w${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`,
      from: anchor,
      direction_deg: initialDirDeg,
      bend_points_count: 0,
      bends: [],
      cut_at_index: null,
      z_order: maxZ + 1,
    };
    this.currentProject.wires.push(wire);
    this.placingWire = { uid: wire.uid, phase: 'floating', initialDirDeg };
    this.setSelection({ kind: 'wire', wire_uid: wire.uid });
    this.rebuild();
    this.container.style.cursor = 'crosshair';
    this.container.focus();
    this.onPlacingWireChange?.(true);
  }

  /** Cancela colocación de cable en curso (elimina el cable pendiente). */
  cancelWirePlacement(): void {
    if (!this.placingWire || !this.currentProject) return;
    this.pendingUndo = null;
    const uid = this.placingWire.uid;
    if (this.currentProject.wires) {
      this.currentProject.wires = this.currentProject.wires.filter((w) => w.uid !== uid);
    }
    this.placingWire = null;
    this.setSelection(null);
    this.rebuild();
    this.container.style.cursor = '';
    this.onPlacingWireChange?.(false);
  }

  /** Indica si hay una colocación de cable en curso. */
  isPlacingWire(): boolean {
    return this.placingWire !== null;
  }

  /** Activa el modo colocación de soldadura. */
  startSolderPlacement(): void {
    if (this.placingWire) this.cancelWirePlacement();
    this.cancelPlacement();
    this.beginPendingUndo();
    this.placingSolder = true;
    this.container.style.cursor = 'crosshair';
    // Bola de vista previa: se dibuja en el overlay y se mueve con el
    // puntero. Radio inicial = máximo (0.375·gridStep); se recalcula
    // en cada mouseMove según el pin/cable bajo el cursor.
    const previewR = this.gridStep * 0.375;
    this.solderPreview = renderSolderBall('__preview__', previewR);
    this.solderPreview.opacity(0.85);
    this.solderPreview.listening(false);
    const world = this.pointerWorld();
    if (world) this.solderPreview.position({ x: world.x, y: world.y });
    else this.solderPreview.visible(false);
    this.overlayLayer.add(this.solderPreview);
    this.overlayLayer.batchDraw();
    this.container.focus();
    this.onPlacingSolderChange?.(true);
  }

  /** Sale del modo colocación de soldadura sin colocar nada. */
  cancelSolderPlacement(): void {
    if (!this.placingSolder) return;
    this.pendingUndo = null;
    this.placingSolder = false;
    this.container.style.cursor = '';
    if (this.solderPreview) {
      this.solderPreview.destroy();
      this.solderPreview = null;
      this.overlayLayer.batchDraw();
    }
    this.onPlacingSolderChange?.(false);
  }

  isPlacingSolder(): boolean {
    return this.placingSolder;
  }

  /**
   * Coloca una bola de soldadura en el vértice más cercano al punto
   * `clickWorld` dentro del path del pin o cable identificado por
   * `hit`. Si el hit no aplica (body de componente, etc.) se ignora.
   * Termina el modo colocación tras colocar la bola.
   */
  private placeSolderAt(hit: HitInfo, clickWorld: { x: number; y: number }): void {
    if (!this.currentProject) return;

    let anchor: SolderAnchor | null = null;

    if (hit.wire_uid) {
      // CABLE: path directamente en world-coords (attachment absoluto).
      const wire = this.findWire(hit.wire_uid);
      if (!wire) return;
      const { def, state } = wireAsPin(wire);
      const path = computePinPath3D(def, state);
      const nearest = nearestVertex(path.map((v) => ({
        at: v.bend_index,
        pos: { x: v.position.x, y: v.position.y },
      })), clickWorld);
      if (!nearest) return;
      anchor = { kind: 'wire', wire_uid: hit.wire_uid, at: nearest.at };
    } else if (hit.kind === 'pin' || hit.kind === 'bend') {
      const inst = this.findInstance(hit.component_uid);
      const def = inst ? this.library.get(inst.def_id) : undefined;
      if (!inst || !def || !hit.pin_id) return;
      const pinIndex = def.pins.findIndex((p) => p.id === hit.pin_id);
      if (pinIndex < 0) return;
      const pinDef = def.pins[pinIndex]!;
      const pinState = inst.pins[pinIndex];
      if (!pinState) return;
      const path = computePinPath3D(pinDef, pinState);
      // Convertimos cada vértice a coords world para poder medir
      // distancia frente al click.
      const worldVertices = path.map((v) => ({
        at: v.bend_index,
        pos: projectLocalToScene(v.position, inst.position_mm, inst.rotation_deg, inst.flipped ?? false),
      }));
      const nearest = nearestVertex(worldVertices, clickWorld);
      if (!nearest) return;
      anchor = {
        kind: 'pin',
        component_uid: hit.component_uid,
        pin_id: hit.pin_id,
        at: nearest.at,
      };
    }

    if (!anchor) return;
    if (!this.currentProject.solder_joints) this.currentProject.solder_joints = [];
    this.commitPendingUndo();
    this.currentProject.solder_joints.push({
      uid: `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`,
      anchor,
      diameter_mm: null,
    });
    this.placingSolder = false;
    this.container.style.cursor = '';
    if (this.solderPreview) {
      this.solderPreview.destroy();
      this.solderPreview = null;
    }
    this.onPlacingSolderChange?.(false);
    this.rebuild();
  }

  /**
   * Dado un hit del cursor, calcula la posición mundo del vértice más
   * cercano al `clickWorld` y el radio que tendría la bola si se
   * colocase allí. Devuelve null si el hit no aplica.
   */
  private previewSolderAnchor(
    hit: HitInfo,
    clickWorld: { x: number; y: number },
  ): { pos: { x: number; y: number }; radius: number } | null {
    if (hit.wire_uid) {
      const wire = this.findWire(hit.wire_uid);
      if (!wire) return null;
      const { def, state } = wireAsPin(wire);
      const path = computePinPath3D(def, state);
      const nearest = nearestVertex(path.map((v) => ({
        at: v.bend_index,
        pos: { x: v.position.x, y: v.position.y },
      })), clickWorld);
      if (!nearest) return null;
      const anchor: SolderAnchor = { kind: 'wire', wire_uid: hit.wire_uid, at: nearest.at };
      return { pos: nearest.pos, radius: this.defaultSolderRadius(anchor) };
    }
    if (hit.kind === 'pin' || hit.kind === 'bend') {
      const inst = this.findInstance(hit.component_uid);
      const def = inst ? this.library.get(inst.def_id) : undefined;
      if (!inst || !def || !hit.pin_id) return null;
      const pinIndex = def.pins.findIndex((p) => p.id === hit.pin_id);
      if (pinIndex < 0) return null;
      const pinDef = def.pins[pinIndex]!;
      const pinState = inst.pins[pinIndex];
      if (!pinState) return null;
      const path = computePinPath3D(pinDef, pinState);
      const worldVertices = path.map((v) => ({
        at: v.bend_index,
        pos: projectLocalToScene(v.position, inst.position_mm, inst.rotation_deg, inst.flipped ?? false),
      }));
      const nearest = nearestVertex(worldVertices, clickWorld);
      if (!nearest) return null;
      const anchor: SolderAnchor = {
        kind: 'pin', component_uid: hit.component_uid, pin_id: hit.pin_id, at: nearest.at,
      };
      return { pos: nearest.pos, radius: this.defaultSolderRadius(anchor) };
    }
    return null;
  }

  /** Mueve el componente / grupo / cable seleccionado por encima de todos los demás. */
  moveSelectedToTop(): void {
    this.pushHistory();
    if (!this.currentProject) return;
    // Con cables y componentes compartiendo el mismo espacio z_order,
    // "traer al frente" tiene que asignar valores por encima de TODOS
    // los ítems del mundo, no sólo de los del mismo tipo. Si no,
    // llevar un wire "al frente" no lo pondría encima de los
    // componentes (y viceversa).
    const globalMaxZ = this.currentWorldMaxZ();
    const sel = this.selection;
    if (sel && (sel.kind === 'wire' || sel.kind === 'wire-bend')) {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      wire.z_order = globalMaxZ + 1;
      this.rebuild();
      return;
    }
    const uids = selectedComponentUids(sel);
    const wireUids = selectedWireUids(sel);
    if (uids.length === 0 && wireUids.length === 0) return;
    let z = globalMaxZ;
    for (const uid of uids) {
      const inst = this.findInstance(uid);
      if (inst) inst.z_order = ++z;
    }
    if (this.currentProject.wires) {
      for (const wu of wireUids) {
        const wire = this.findWire(wu);
        if (wire) wire.z_order = ++z;
      }
    }
    this.rebuild();
  }

  /** Mueve el componente / grupo / cable seleccionado por debajo de todos los demás. */
  moveSelectedToBottom(): void {
    this.pushHistory();
    if (!this.currentProject) return;
    const globalMinZ = this.currentWorldMinZ();
    const sel = this.selection;
    if (sel && (sel.kind === 'wire' || sel.kind === 'wire-bend')) {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      wire.z_order = globalMinZ - 1;
      this.rebuild();
      return;
    }
    const uids = selectedComponentUids(sel);
    const wireUids = selectedWireUids(sel);
    if (uids.length === 0 && wireUids.length === 0) return;
    let z = globalMinZ;
    for (const uid of uids) {
      const inst = this.findInstance(uid);
      if (inst) inst.z_order = --z;
    }
    if (this.currentProject.wires) {
      for (const wu of wireUids) {
        const wire = this.findWire(wu);
        if (wire) wire.z_order = --z;
      }
    }
    this.rebuild();
  }

  private currentWorldMaxZ(): number {
    if (!this.currentProject) return 0;
    let m = 0;
    for (const c of this.currentProject.components) if (c.z_order > m) m = c.z_order;
    for (const w of this.currentProject.wires ?? []) if (w.z_order > m) m = w.z_order;
    return m;
  }

  private currentWorldMinZ(): number {
    if (!this.currentProject) return 0;
    let m = 0;
    for (const c of this.currentProject.components) if (c.z_order < m) m = c.z_order;
    for (const w of this.currentProject.wires ?? []) if (w.z_order < m) m = w.z_order;
    return m;
  }

  /**
   * Alterna el flag `flipped` sobre el/los componente(s) seleccionado(s).
   * En multi-selección se aplica flip como espejo respecto al eje
   * vertical que pasa por el centro del grupo → cada componente
   * invierte su flag y su posición X se refleja respecto al centro.
   */
  flipSelected(): void {
    this.pushHistory();
    this.flipSelectedImpl();
    this.resyncInteractionBaselines();
  }

  private flipSelectedImpl(): void {
    if (!this.currentProject) return;
    const sel = this.selection;
    // Cable / wire-bend: espejo horizontal en torno al punto medio
    // del cable → direction_deg = 180 - dir; cada bend cambia el
    // signo de angle_in_plane_deg; `from` se refleja respecto al
    // eje vertical que pasa por el punto medio.
    if (sel && (sel.kind === 'wire' || sel.kind === 'wire-bend')) {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      const wp = wireAsPin(wire);
      const path = computePinPath3D(wp.def, wp.state);
      const tip = path[path.length - 1]!.position;
      const pivotX = (wire.from.x + tip.x) / 2;
      wire.from = {
        x: 2 * pivotX - wire.from.x,
        y: wire.from.y,
      };
      wire.direction_deg = ((180 - wire.direction_deg) % 360 + 360) % 360;
      wire.bends = wire.bends.map((b) => ({
        angle_in_plane_deg: -b.angle_in_plane_deg as typeof b.angle_in_plane_deg,
        angle_out_of_plane_deg: b.angle_out_of_plane_deg,
      }));
      wire.from = snapPointToGrid(wire.from, this.gridStep);
      this.rebuild();
      return;
    }
    const uids = selectedComponentUids(sel);
    const wireUidsForFlip = selectedWireUids(sel);
    if (uids.length === 0 && wireUidsForFlip.length === 0) return;

    const insts = uids.map((u) => this.findInstance(u)).filter((x): x is ComponentInstance => !!x);
    const wires = wireUidsForFlip.map((u) => this.findWire(u)).filter((x): x is Wire => !!x);
    const instRefs = insts.map((i) => this.anchorOf(i));
    const wireRefs = wires.map((w) => ({ x: w.from.x, y: w.from.y }));
    const refs = [...instRefs, ...wireRefs];
    if (refs.length === 0) return;

    // Espejo respecto a la vertical que pasa por el pivote (el elemento
    // más central), que es el único que se ajusta a rejilla y queda fijo.
    const pivot = mostCentralRef(refs);
    const p0 = snapPointToGrid(pivot, this.gridStep);
    const preX = p0.x - pivot.x;
    const preY = p0.y - pivot.y;
    const mirror = (r: { x: number; y: number }) => ({
      x: 2 * p0.x - (r.x + preX),
      y: r.y + preY,
    });

    for (let i = 0; i < insts.length; i++) {
      const inst = insts[i]!;
      // Un espejo horizontal NO es sólo alternar `flipped`. En el
      // render, world = pos + R(rot)·S·local con S = diag(1, ±1); para
      // que M·R(rot)·S (M = espejo en X) sea de nuevo de esa forma hace
      // falta además rot' = 180 − rot. Sin ese término los componentes
      // a 0° y 180° quedaban mal orientados (los de ±90° coincidían por
      // casualidad) y el bloque se abría; repetir el flip lo deshacía
      // porque 180 − (180 − rot) = rot.
      const flipped = !(inst.flipped ?? false);
      const rot = normalizeBodyRotation(180 - inst.rotation_deg);
      inst.flipped = flipped;
      inst.rotation_deg = rot;
      inst.position_mm = this.positionForAnchor(inst, mirror(instRefs[i]!), rot, flipped);
    }
    for (let i = 0; i < wires.length; i++) {
      const wire = wires[i]!;
      const a = mirror(wireRefs[i]!);
      wire.from = { x: roundMm(a.x), y: roundMm(a.y) };
      wire.direction_deg = ((180 - wire.direction_deg) % 360 + 360) % 360;
      wire.bends = wire.bends.map((b) => ({
        angle_in_plane_deg: -b.angle_in_plane_deg as typeof b.angle_in_plane_deg,
        angle_out_of_plane_deg: b.angle_out_of_plane_deg,
      }));
    }
    this.rebuild();
  }

  /**
   * Rota el/los componente(s) seleccionado(s) 90° CW o CCW.
   * En multi-selección los componentes rotan sobre el centro del
   * grupo: cada uno gira 90° sobre sí mismo Y su posición gira
   * alrededor del centro del grupo.
   */
  rotateSelected(delta: 90 | -90): void {
    this.pushHistory();
    this.rotateSelectedImpl(delta);
    // Si se está arrastrando con el ratón a la vez, la foto de
    // referencia del arrastre ha quedado obsoleta: re-anclarla.
    this.resyncInteractionBaselines();
  }

  private rotateSelectedImpl(delta: 90 | -90): void {
    if (!this.currentProject) return;
    const sel = this.selection;
    // Cable solo: rota `direction_deg` en ±90 y gira `from` alrededor
    // del pivote (punto medio del cable calculado desde su path).
    if (sel && (sel.kind === 'wire' || sel.kind === 'wire-bend')) {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      const wp = wireAsPin(wire);
      const path = computePinPath3D(wp.def, wp.state);
      const tip = path[path.length - 1]!.position;
      const pivot = { x: (wire.from.x + tip.x) / 2, y: (wire.from.y + tip.y) / 2 };
      const rad = delta * Math.PI / 180;
      const c = Math.cos(rad), s = Math.sin(rad);
      const dx = wire.from.x - pivot.x;
      const dy = wire.from.y - pivot.y;
      wire.from = {
        x: pivot.x + dx * c - dy * s,
        y: pivot.y + dx * s + dy * c,
      };
      wire.direction_deg = ((wire.direction_deg + delta) % 360 + 360) % 360;
      // Snap del `from` a rejilla por si el pivote no era grid-aligned.
      wire.from = snapPointToGrid(wire.from, this.gridStep);
      this.rebuild();
      return;
    }
    const uids = selectedComponentUids(this.selection);
    const wireUidsRot = selectedWireUids(this.selection);
    if (uids.length === 0 && wireUidsRot.length === 0) return;
    if (uids.length === 1 && wireUidsRot.length === 0) {
      const inst = this.findInstance(uids[0]!);
      if (!inst) return;
      inst.rotation_deg = normalizeBodyRotation(inst.rotation_deg + delta);
      const def = this.library.get(inst.def_id);
      if (def) inst.position_mm = this.snapToGridForComponent(inst, def, inst.position_mm);
      this.rebuild();
      return;
    }
    const insts = uids.map((u) => this.findInstance(u)).filter((x): x is ComponentInstance => !!x);
    const wireUids = wireUidsRot;
    const wires = wireUids.map((u) => this.findWire(u)).filter((x): x is Wire => !!x);
    // Rotación de bloque.
    //
    // Regla: al girar NADA se ajusta a la rejilla salvo UN componente
    // pivote. Todos los demás elementos (componentes y cables) se
    // recolocan de forma puramente relativa a él. Al ser un único giro
    // rígido alrededor de un único punto, las distancias entre
    // elementos no pueden cambiar — da igual cuántas vueltas se den.
    //
    // El pivote es el componente cuyo punto de anclaje queda más cerca
    // del centro del grupo. La elección es estable entre giros: el giro
    // conserva distancias, así que el mismo componente sigue siendo el
    // más central. Y como el pivote es el PUNTO FIJO del giro, 4 vueltas
    // devuelven el bloque exactamente a su estado inicial.
    //
    // El giro usa `rotateVec90` (permuta/negación de coordenadas) en vez
    // de cos/sin: `Math.cos(90°)` vale 6.1e-17, no 0, y ese residuo se
    // acumulaba vuelta a vuelta.
    // El punto de referencia de cada elemento es su ANCLA DE REJILLA, no
    // su `position_mm`: para un componente, el `pin[0]` en world coords
    // (que `snapToGridForComponent` mantiene siempre sobre un nodo de la
    // rejilla); para un cable, su `from`. Esos puntos son múltiplos
    // exactos del paso de rejilla, así que el giro se resuelve sobre
    // ENTEROS — número de pasos — y girar es permutar y negar dos
    // enteros. `position_mm` se DERIVA después del ancla girada.
    const instRefs = insts.map((i) => this.anchorOf(i));
    const wireRefs = wires.map((w) => ({ x: w.from.x, y: w.from.y }));
    const refs = [...instRefs, ...wireRefs];
    if (refs.length === 0) return;

    // ÚNICO ajuste a rejilla de toda la operación: el del pivote. El
    // bloque entero se traslada por ese mismo delta (traslación
    // uniforme ⇒ no altera ninguna posición relativa).
    const pivot = mostCentralRef(refs);
    const p0 = snapPointToGrid(pivot, this.gridStep);
    const turn = this.blockRotator(pivot, p0, delta);

    for (let i = 0; i < insts.length; i++) {
      const inst = insts[i]!;
      const a = turn(instRefs[i]!);
      const rot = normalizeBodyRotation(inst.rotation_deg + delta);
      inst.rotation_deg = rot;
      inst.position_mm = this.positionForAnchor(inst, a, rot, inst.flipped ?? false);
    }
    for (let i = 0; i < wires.length; i++) {
      const wire = wires[i]!;
      const a = turn(wireRefs[i]!);
      wire.from = { x: roundMm(a.x), y: roundMm(a.y) };
      wire.direction_deg = ((wire.direction_deg + delta) % 360 + 360) % 360;
    }
    this.rebuild();
  }

  /** Ancla de rejilla de un componente (su `pin[0]` en world coords). */
  private anchorOf(inst: ComponentInstance): { x: number; y: number } {
    return this.pinAnchorWorld(inst) ?? { x: inst.position_mm.x, y: inst.position_mm.y };
  }

  /**
   * `position_mm` tal que el `pin[0]` del componente caiga exactamente
   * en `anchor` con la rotación y el flip indicados. Es la inversa de
   * `anchorOf`: las transformaciones de bloque calculan el ancla nueva
   * y derivan de ella la posición, en vez de mover `position_mm`
   * directamente (que nunca cae en rejilla).
   */
  private positionForAnchor(
    inst: ComponentInstance,
    anchor: { x: number; y: number },
    rot: BodyRotation,
    flipped: boolean,
  ): { x: number; y: number } {
    const pin = this.library.get(inst.def_id)?.pins[0];
    if (!pin) return { x: roundMm(anchor.x), y: roundMm(anchor.y) };
    const local = { x: pin.attachment.x, y: flipped ? -pin.attachment.y : pin.attachment.y };
    const r = rotateVec90(local, rot);
    return { x: roundMm(anchor.x - r.x), y: roundMm(anchor.y - r.y) };
  }

  /**
   * Devuelve la función que gira un ancla ±90° alrededor de `p0`, con el
   * bloque previamente trasladado para que `pivot` caiga en `p0`.
   * El desplazamiento se separa en pasos de rejilla ENTEROS (girados de
   * forma exacta) más un residuo, que normalmente es 0.
   */
  private blockRotator(
    pivot: { x: number; y: number },
    p0: { x: number; y: number },
    delta: 90 | -90,
  ): (r: { x: number; y: number }) => { x: number; y: number } {
    const step = this.gridStep;
    const preX = p0.x - pivot.x;
    const preY = p0.y - pivot.y;
    return (r) => {
      const relX = r.x + preX - p0.x;
      const relY = r.y + preY - p0.y;
      const nx = Math.round(relX / step);
      const ny = Math.round(relY / step);
      const n = rotateVec90({ x: nx, y: ny }, delta);
      const res = rotateVec90({ x: relX - nx * step, y: relY - ny * step }, delta);
      return { x: p0.x + n.x * step + res.x, y: p0.y + n.y * step + res.y };
    };
  }

  /**
   * Posición en world-coords del `pin[0].attachment` de una instancia.
   * Se usa como ancla de rejilla del bloque: es el mismo punto que
   * `snapToGridForComponent` alinea al colocar un componente suelto.
   */
  private pinAnchorWorld(inst: ComponentInstance): { x: number; y: number } | null {
    const def = this.library.get(inst.def_id);
    const pin = def?.pins[0];
    if (!pin) return null;
    const local = {
      x: pin.attachment.x,
      y: inst.flipped ? -pin.attachment.y : pin.attachment.y,
    };
    const r = rotateVec90(local, inst.rotation_deg);
    return { x: inst.position_mm.x + r.x, y: inst.position_mm.y + r.y };
  }

  /**
   * Ángulo (en grados, convención Konva Y-down, +X = 0°) del
   * segmento del pin que ENTRA al bend seleccionado, medido en
   * coordenadas de mundo. Devuelve null si no hay bend seleccionado.
   * Se usa para orientar el icono de los botones de doblado.
   */
  getSelectedBendWorldAngle(): number | null {
    const sel = this.selection;
    if (!sel) return null;
    // Cable: la path del cable ya está en world coords (attachment
    // absoluto), así que el forward es directamente el ángulo mundo.
    if (sel.kind === 'wire-bend') {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return null;
      const { def, state } = wireAsPin(wire);
      const path = computePinPath3D(def, state);
      // `resolveBendDirection` necesita una referencia que NO gire al
      // doblar; si no, el remapeo del botón se invierte en pulsaciones
      // alternas y el cable oscila en vez de avanzar. La referencia
      // válida es siempre el tramo que permanece QUIETO:
      //   - side='right': se mueve la cola → referencia = tramo que
      //     ENTRA al bend, o sea path[bend_index].forward.
      //   - side='left':  se mueve la cabeza → referencia = tramo que
      //     SALE del bend (la cola), o sea el forward del vértice
      //     etiquetado con este bend_index. Se le suman 180° porque
      //     el brazo que se mueve sale del pivote en sentido opuesto
      //     a la cola.
      const refVertex =
        sel.side === 'left'
          ? path.find((v) => v.bend_index === sel.bend_index)
          : path[sel.bend_index];
      if (!refVertex) return null;
      const deg = (Math.atan2(refVertex.forward.y, refVertex.forward.x) * 180) / Math.PI;
      return sel.side === 'left' ? deg + 180 : deg;
    }
    if (sel.kind !== 'bend') return null;
    const inst = this.findInstance(sel.component_uid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) return null;
    const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
    if (pinIndex < 0) return null;
    const pinDef = def.pins[pinIndex];
    const pinState = inst.pins[pinIndex];
    if (!pinDef || !pinState) return null;
    const path = computePinPath3D(pinDef, pinState);
    const refVertex = path[sel.bend_index];
    if (!refVertex) return null;
    const fLx = refVertex.forward.x;
    const fLy = refVertex.forward.y;
    const flippedFy = inst.flipped ? -fLy : fLy;
    const rot = inst.rotation_deg * Math.PI / 180;
    const c = Math.cos(rot);
    const s = Math.sin(rot);
    const wfx = fLx * c - flippedFy * s;
    const wfy = fLx * s + flippedFy * c;
    return Math.atan2(wfy, wfx) * 180 / Math.PI;
  }

  bendSelected(direction: BendDirection): void {
    this.pushHistory();
    const sel = this.selection;
    if (!sel) return;
    // Caso cable: el bend está en el propio wire.
    if (sel.kind === 'wire-bend') {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      // Un cable no está "flipped", así que no hay que remapear el
      // signo. bendOp opera sobre un PinState equivalente.
      const pseudoState = { pin_id: wire.uid, bends: wire.bends, cut_at_index: wire.cut_at_index };

      // Lado por defecto ('right'): bendOp gira los segmentos DESPUÉS
      // del bend seleccionado — la cola del cable se mueve, el origen
      // (`from`) queda fijo.
      //
      // Lado 'left': queremos justo lo contrario, que se mueva la
      // parte del cable que va de `from` hasta el bend, y que la cola
      // (bend..tip) permanezca donde está.
      //
      // Truco: aplicar el bend con dirección OPUESTA (que rota la
      // cola +α inverso) y luego rotar TODO el cable alrededor del
      // bend point por α. El resultado es que la cola vuelve a su
      // sitio (rotación + rotación inversa = identidad) y el resto
      // del cable queda rotado α respecto al bend point, que es
      // exactamente lo pedido. `from` se recalcula porque el cable
      // rota entero.
      if (sel.side === 'left') {
        // Posición del bend point (mundo) ANTES de tocar nada — la
        // rotación posterior no lo mueve, así que es válida como pivote.
        const wp = wireAsPin(wire);
        const pathBefore = computePinPath3D(wp.def, wp.state);
        const bendVertex = pathBefore.find((v) => v.bend_index === sel.bend_index);
        if (!bendVertex) return;
        const pivot = { x: bendVertex.position.x, y: bendVertex.position.y };
        const oppositeDir: BendDirection = direction === 'left' ? 'right' : direction === 'right' ? 'left' : direction;
        const next = bendOp(pseudoState, sel.bend_index, oppositeDir);
        // `bendOp` puede RECHAZAR el doblez (clamp a ±135°, o cable
        // cortado en/antes de este bend) y devolver el estado igual.
        // En ese caso no debe hacerse nada: si rotásemos el cable de
        // todas formas, la compensación no tendría un doblez que
        // cancelar y giraría el cable ENTERO — incluida la mitad que
        // debe permanecer fija.
        const angBefore = pseudoState.bends[sel.bend_index]?.angle_in_plane_deg;
        const angAfter = next.bends[sel.bend_index]?.angle_in_plane_deg;
        if (angBefore === angAfter) return;
        wire.bends = next.bends;
        wire.cut_at_index = next.cut_at_index;
        // Ángulo (grados) con signo Konva Y-down: right = +45, left = -45.
        const alphaDeg = direction === 'right' ? 45 : direction === 'left' ? -45 : 0;
        if (alphaDeg !== 0) this.rotateWireAround(wire, pivot, alphaDeg);
        // La rotación deja la cola con la orientación correcta, pero el
        // bend point se ha desplazado: al pasar de axiales a diagonales
        // (o al revés) los segmentos del lado que se mueve cambian de
        // longitud (2.54 vs 2.54·√2), y ese cambio empuja el resto de
        // la trayectoria. Se cierra con una traslación que devuelve el
        // bend point a su sitio — así la cola queda EXACTAMENTE donde
        // estaba y sólo se mueve el lado izquierdo.
        const wpAfter = wireAsPin(wire);
        const pathAfter = computePinPath3D(wpAfter.def, wpAfter.state);
        const bendAfter = pathAfter.find((v) => v.bend_index === sel.bend_index);
        if (bendAfter) {
          wire.from = {
            x: wire.from.x + (pivot.x - bendAfter.position.x),
            y: wire.from.y + (pivot.y - bendAfter.position.y),
          };
        }
        this.rebuild();
        return;
      }

      const next = bendOp(pseudoState, sel.bend_index, direction);
      wire.bends = next.bends;
      wire.cut_at_index = next.cut_at_index;
      this.rebuild();
      return;
    }
    if (sel.kind !== 'bend') return;
    const inst = this.findInstance(sel.component_uid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) return;
    const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
    if (pinIndex < 0) return;
    if (def.pins[pinIndex]?.locked) return;
    const currentState = inst.pins[pinIndex];
    if (!currentState) return;
    // Los segmentos del pin ya se auto-alinean a rejilla (axial =
    // grid-step, diagonal = √2 · grid-step), así que la doblez ±45°
    // basta: no hace falta ajustar ni el tip ni bends intermedios.
    const localDir = this.mapWorldBendToLocal(inst, def, pinIndex, sel.bend_index, direction);
    inst.pins[pinIndex] = bendOp(inst.pins[pinIndex]!, sel.bend_index, localDir);
    this.rebuild();
  }

  /**
   * Semántica de las flechas de doblez:
   *   ⇨ (right)  → el pin gira EN SENTIDO HORARIO en pantalla.
   *   ⇦ (left)   → el pin gira EN SENTIDO ANTIHORARIO en pantalla.
   * Independiente de la rotación o posición del componente. Una
   * rotación pura preserva el sentido de giro visual, así que sólo
   * el flip (scaleY = -1) invierte el signo local: en un componente
   * espejado, un +α local se ve como antihorario en pantalla.
   */
  private mapWorldBendToLocal(
    inst: ComponentInstance,
    _def: ComponentDef,
    _pinIndex: number,
    _bendIndex: number,
    worldDir: BendDirection,
  ): BendDirection {
    if (worldDir !== 'left' && worldDir !== 'right') return worldDir;
    if (!inst.flipped) return worldDir;
    return worldDir === 'right' ? 'left' : 'right';
  }

  /** Punto del extremo libre (tip) de un cable, en coords mundo. */
  private wireTipPoint(wire: Wire): { x: number; y: number } | null {
    const wp = wireAsPin(wire);
    const path = computePinPath3D(wp.def, wp.state);
    const last = path[path.length - 1];
    return last ? { x: last.position.x, y: last.position.y } : null;
  }

  /**
   * Cruz de mira sobre un punto del lienzo. Se usa durante la
   * colocación de un cable para que se vea exactamente en qué nodo de
   * rejilla va a aterrizar el extremo al hacer click.
   */
  private drawCrosshair(p: { x: number; y: number }): void {
    const arm = this.gridStep * 0.7;
    const common = {
      stroke: '#d9700f',
      strokeWidth: 0.22,
      listening: false,
      lineCap: 'round' as const,
    };
    this.overlayLayer.add(new Konva.Line({ points: [p.x - arm, p.y, p.x + arm, p.y], ...common }));
    this.overlayLayer.add(new Konva.Line({ points: [p.x, p.y - arm, p.x, p.y + arm], ...common }));
    this.overlayLayer.add(new Konva.Circle({
      x: p.x,
      y: p.y,
      radius: arm * 0.34,
      stroke: '#d9700f',
      strokeWidth: 0.22,
      listening: false,
    }));
  }

  /**
   * Devuelve el bend point del cable más cercano a un punto del mundo,
   * junto con su distancia (en mm mundo). null si el cable no tiene
   * bend points o no existe.
   */
  private nearestWireBend(
    wireUid: Uid,
    world: { x: number; y: number },
  ): { bend_index: number; dist: number } | null {
    const wire = this.findWire(wireUid);
    if (!wire) return null;
    const wp = wireAsPin(wire);
    const path = computePinPath3D(wp.def, wp.state);
    let best: { bend_index: number; dist: number } | null = null;
    for (const v of path) {
      if (typeof v.bend_index !== 'number') continue;
      const d = Math.hypot(world.x - v.position.x, world.y - v.position.y);
      if (!best || d < best.dist) best = { bend_index: v.bend_index, dist: d };
    }
    return best;
  }

  /**
   * Decide de qué lado del bend point cae un punto del mundo:
   * izquierda (segmentos 0..bend_index) o derecha (bend_index..fin).
   * Se usa cuando el usuario, teniendo un bend seleccionado, toca en
   * el cuerpo del cable — así el editor sabe a qué lado cambiar el
   * "lado activo" para el próximo doblado.
   */
  private wireSideForWorldPoint(
    wireUid: Uid,
    bendIndex: number,
    world: { x: number; y: number },
  ): 'left' | 'right' | null {
    const wire = this.findWire(wireUid);
    if (!wire) return null;
    const wp = wireAsPin(wire);
    const path = computePinPath3D(wp.def, wp.state);
    const splitAt = path.findIndex((v) => v.bend_index === bendIndex);
    if (splitAt < 0) return null;
    // Casos degenerados: el bend es el primer o el último vértice, con
    // lo que uno de los dos lados está vacío y el otro es el único
    // posible (pasa con cables cortados justo en ese bend).
    if (splitAt < 1) return 'right';
    if (splitAt >= path.length - 1) return 'left';
    const distToPolyline = (from: number, to: number): number => {
      let best = Infinity;
      for (let i = from; i < to; i++) {
        const a = path[i]!.position;
        const b = path[i + 1]!.position;
        const d = distancePointToSegment(world, a, b);
        if (d < best) best = d;
      }
      return best;
    };
    const dLeft = distToPolyline(0, splitAt);
    const dRight = distToPolyline(splitAt, path.length - 1);
    return dLeft < dRight ? 'left' : 'right';
  }

  /**
   * Aplica una rotación rígida (mismo signo Y-down que Konva) al
   * cable entero alrededor de un punto pivote en coordenadas mundo:
   * gira `from` alrededor del pivote y suma `angleDeg` a la
   * `direction_deg` inicial. Los `bends[]` (relativos entre segmentos
   * consecutivos) se preservan sin cambios porque una rotación rígida
   * mantiene la geometría relativa.
   */
  private rotateWireAround(wire: Wire, pivot: { x: number; y: number }, angleDeg: number): void {
    const rad = (angleDeg * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const dx = wire.from.x - pivot.x;
    const dy = wire.from.y - pivot.y;
    wire.from = {
      x: pivot.x + dx * c - dy * s,
      y: pivot.y + dx * s + dy * c,
    };
    wire.direction_deg = wire.direction_deg + angleDeg;
  }

  cutSelected(): void {
    this.pushHistory();
    const sel = this.selection;
    if (!sel) return;
    if (sel.kind === 'wire-bend') {
      // En cables, "cortar" no elimina un lado (no habría manera
      // ambigua de decidir cuál) sino que DIVIDE el cable en dos
      // cables independientes. El bend seleccionado se convierte
      // en la unión: quedan `wire.from → bend[i]` y `bend[i] → tip`.
      this.splitWireAt(sel.wire_uid, sel.bend_index);
      return;
    }
    if (sel.kind !== 'bend') return;
    const inst = this.findInstance(sel.component_uid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) return;
    const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
    if (pinIndex < 0) return;
    if (def.pins[pinIndex]?.locked) return;
    const currentState = inst.pins[pinIndex];
    if (!currentState) return;
    inst.pins[pinIndex] = cutOp(currentState, sel.bend_index);
    this.rebuild();
  }

  /**
   * Elimina el/los componente(s) seleccionado(s) del proyecto.
   * Limpia la selección. Si se está en modo colocación se cancela.
   */
  deleteSelected(): void {
    this.pushHistory();
    if (!this.currentProject) return;
    const sel = this.selection;
    // Caso soldadura: eliminar la bola. Tras quitarla se restaura la
    // selección al bend o wire-bend subyacente para que la siguiente
    // acción actúe sobre el pin/cable de forma natural.
    if (sel && sel.kind === 'solder') {
      const solder = this.findSolder(sel.solder_uid);
      if (!solder) return;
      const anchor = solder.anchor;
      this.currentProject.solder_joints =
        (this.currentProject.solder_joints ?? []).filter((s) => s.uid !== sel.solder_uid);
      // Restaurar selección al vértice subyacente cuando es un bend
      // (no lo hacemos para 'attachment'/'tip' porque no hay tipo
      // 'pin-tip' en la selección; se limpia).
      if (typeof anchor.at === 'number') {
        if (anchor.kind === 'pin') {
          this.setSelection({
            kind: 'bend',
            component_uid: anchor.component_uid,
            pin_id: anchor.pin_id,
            bend_index: anchor.at,
          });
        } else {
          this.setSelection({
            kind: 'wire-bend',
            wire_uid: anchor.wire_uid,
            bend_index: anchor.at,
            side: 'right',
          });
        }
      } else {
        this.setSelection(null);
      }
      this.rebuild();
      return;
    }
    // Caso cable (o bend de cable): borra el cable seleccionado.
    if (sel && (sel.kind === 'wire' || sel.kind === 'wire-bend')) {
      const wUid = sel.wire_uid;
      if (this.currentProject.wires) {
        this.currentProject.wires = this.currentProject.wires.filter((w) => w.uid !== wUid);
      }
      if (this.placingWire && this.placingWire.uid === wUid) {
        this.placingWire = null;
        this.container.style.cursor = '';
        this.onPlacingWireChange?.(false);
      }
      this.setSelection(null);
      this.rebuild();
      return;
    }
    const uids = new Set(selectedComponentUids(this.selection));
    const wireUids = new Set(selectedWireUids(this.selection));
    if (uids.size === 0 && wireUids.size === 0) return;
    if (wireUids.size > 0 && this.currentProject.wires) {
      this.currentProject.wires = this.currentProject.wires.filter((w) => !wireUids.has(w.uid));
    }
    if (uids.size === 0) {
      this.dragging = null;
      this.setSelection(null);
      this.rebuild();
      return;
    }
    if (this.placing && uids.has(this.placing.uid)) {
      // Si el líder del placement está en el set, cancelamos primero.
      this.placing = null;
      this.container.style.cursor = '';
    }
    this.currentProject.components = this.currentProject.components.filter(
      (c) => !uids.has(c.uid),
    );
    this.dragging = null;
    this.setSelection(null);
    this.rebuild();
  }

  /** Restaura TODOS los pines editables del/los componente(s) seleccionado(s). */
  restoreComponent(): void {
    this.pushHistory();
    const uids = selectedComponentUids(this.selection);
    if (uids.length === 0) return;
    for (const uid of uids) {
      const inst = this.findInstance(uid);
      const def = inst ? this.library.get(inst.def_id) : undefined;
      if (!inst || !def) continue;
      for (let i = 0; i < def.pins.length; i++) {
        const pinDef = def.pins[i];
        const pinState = inst.pins[i];
        if (!pinDef || !pinState || pinDef.locked) continue;
        inst.pins[i] = restoreOp(pinDef, pinState);
        inst.pins[i]!.length_adjust_mm = 0;
        inst.pins[i]!.angle_adjust_deg = 0;
        delete inst.pins[i]!.angle_adjust_at_bend;
        delete inst.pins[i]!.align_offsets;
      }
    }
    this.rebuild();
  }

  /** Cambia una propiedad de instancia (pinout, color, etc.) y repinta. */
  setInstanceProperty(uid: Uid, key: string, value: string): void {
    const inst = this.findInstance(uid);
    if (!inst) return;
    if (!inst.properties) inst.properties = {};
    inst.properties[key] = value;
    this.rebuild();
  }

  restoreSelected(): void {
    this.pushHistory();
    const sel = this.selection;
    if (!sel) return;

    // Cable individual / bend de cable.
    if (sel.kind === 'wire' || sel.kind === 'wire-bend') {
      const wire = this.findWire(sel.wire_uid);
      if (!wire) return;
      wire.bends = wire.bends.map(() => ({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 }));
      wire.cut_at_index = null;
      this.rebuild();
      return;
    }

    // Pin / bend individual de un pin de componente.
    if (sel.kind === 'pin' || sel.kind === 'bend') {
      const inst = this.findInstance(sel.component_uid);
      const def = inst ? this.library.get(inst.def_id) : undefined;
      if (!inst || !def) return;
      const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
      if (pinIndex < 0) return;
      if (def.pins[pinIndex]?.locked) return;
      const pinDef = def.pins[pinIndex]!;
      const currentState = inst.pins[pinIndex];
      if (!currentState) return;
      inst.pins[pinIndex] = restoreOp(pinDef, currentState);
      this.rebuild();
      return;
    }

    // Multi: restaura todos los componentes y todos los cables del
    // bloque (cada uno según su regla). Componente completo → todos
    // sus pines. Cable → sin bends ni corte.
    if (sel.kind === 'multi' || sel.kind === 'component') {
      const compUids = selectedComponentUids(sel);
      for (const uid of compUids) {
        const inst = this.findInstance(uid);
        const def = inst ? this.library.get(inst.def_id) : undefined;
        if (!inst || !def) continue;
        inst.pins = inst.pins.map((state, idx) => {
          const pinDef = def.pins[idx];
          if (!pinDef || pinDef.locked) return state;
          return restoreOp(pinDef, state);
        });
      }
      const wireUids = selectedWireUids(sel);
      for (const wUid of wireUids) {
        const wire = this.findWire(wUid);
        if (!wire) continue;
        wire.bends = wire.bends.map(() => ({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 }));
        wire.cut_at_index = null;
      }
      this.rebuild();
    }
  }

  // ---------- interno ----------

  private measure(): { width: number; height: number } {
    const rect = this.container.getBoundingClientRect();
    return {
      width: Math.max(1, Math.floor(rect.width)),
      height: Math.max(1, Math.floor(rect.height)),
    };
  }

  private handleResize(): void {
    const { width, height } = this.measure();
    this.stage.width(width);
    this.stage.height(height);
    if (!this.centeredOnce && width > 2 && height > 2) {
      this.stage.position({ x: width / 2, y: height / 2 });
      this.centeredOnce = true;
    }
    this.drawGrid();
    this.stage.batchDraw();
  }

  private stageCenter(): { x: number; y: number } {
    return { x: this.stage.width() / 2, y: this.stage.height() / 2 };
  }

  private zoomBy(factor: number, pivotPx: { x: number; y: number }): void {
    const oldScale = this.stage.scaleX();
    const newScale = clamp(oldScale * factor, MIN_SCALE, MAX_SCALE);
    if (newScale === oldScale) return;
    const worldBefore = {
      x: (pivotPx.x - this.stage.x()) / oldScale,
      y: (pivotPx.y - this.stage.y()) / oldScale,
    };
    this.stage.scale({ x: newScale, y: newScale });
    this.stage.position({
      x: pivotPx.x - worldBefore.x * newScale,
      y: pivotPx.y - worldBefore.y * newScale,
    });
    this.drawGrid();
    this.stage.batchDraw();
    this.emitZoom();
  }

  private onWheel(e: Konva.KonvaEventObject<WheelEvent>): void {
    e.evt.preventDefault();
    const pointer = this.stage.getPointerPosition();
    if (!pointer) return;
    const factor = e.evt.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
    this.zoomBy(factor, pointer);
  }

  /**
   * Gestos táctiles (móvil / tablet):
   *   - **1 dedo**: pasa al flujo normal, Konva ya lo traduce a
   *     mousedown/move/up, así que seleccionar y arrastrar funciona
   *     igual que con el ratón.
   *   - **2 dedos**: pinch-to-zoom + pan simultáneo. El punto medio
   *     entre los dos dedos es el pivote del zoom (natural: el sitio
   *     que estás mirando permanece bajo tus dedos), y su
   *     desplazamiento arrastra el lienzo.
   *
   * Al pasar de 1 a 2 dedos se cancela cualquier drag que Konva
   * hubiera iniciado con el primer dedo, para no acabar moviendo un
   * componente sin querer mientras se hace el pinch.
   */
  private pinch: {
    startDist: number;
    startScale: number;
    lastMid: { x: number; y: number };
  } | null = null;

  private setupTouchGestures(): void {
    const el = this.container;
    const bounds = () => el.getBoundingClientRect();
    const distance = (a: Touch, b: Touch) => {
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };
    const midpoint = (a: Touch, b: Touch) => {
      const r = bounds();
      return {
        x: (a.clientX + b.clientX) / 2 - r.left,
        y: (a.clientY + b.clientY) / 2 - r.top,
      };
    };

    el.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length >= 2) {
          // Al arrancar el pinch cancelamos lo que Konva haya iniciado
          // con el primer dedo (seleccionar, arrastrar, rubber band…).
          this.onMouseUp();
          const [a, b] = [e.touches[0]!, e.touches[1]!];
          this.pinch = {
            startDist: distance(a, b) || 1,
            startScale: this.stage.scaleX(),
            lastMid: midpoint(a, b),
          };
          e.preventDefault();
        }
      },
      { passive: false },
    );

    el.addEventListener(
      'touchmove',
      (e) => {
        if (!this.pinch || e.touches.length < 2) return;
        const [a, b] = [e.touches[0]!, e.touches[1]!];
        const dist = distance(a, b) || 1;
        const mid = midpoint(a, b);
        // Zoom con el punto medio como pivote.
        const targetScale = clamp(
          this.pinch.startScale * (dist / this.pinch.startDist),
          MIN_SCALE,
          MAX_SCALE,
        );
        const currentScale = this.stage.scaleX();
        if (targetScale !== currentScale) {
          const factor = targetScale / currentScale;
          this.zoomBy(factor, mid);
        }
        // Pan por delta del punto medio.
        const dx = mid.x - this.pinch.lastMid.x;
        const dy = mid.y - this.pinch.lastMid.y;
        if (dx || dy) {
          this.stage.position({
            x: this.stage.x() + dx,
            y: this.stage.y() + dy,
          });
          this.drawGrid();
          this.stage.batchDraw();
        }
        this.pinch.lastMid = mid;
        e.preventDefault();
      },
      { passive: false },
    );

    const endPinch = (e: TouchEvent) => {
      if (this.pinch && e.touches.length < 2) {
        this.pinch = null;
        // Si aún queda un dedo, no reanudamos ningún drag: el
        // usuario tendrá que levantar y volver a tocar. Es lo
        // esperado tras un pinch.
      }
    };
    el.addEventListener('touchend', endPinch, { passive: true });
    el.addEventListener('touchcancel', endPinch, { passive: true });

    // En portrait el CSS ya pone touch-action:none, pero se lo
    // aseguramos también aquí por si el host lo sobreescribe. Con
    // touch-action:none delegamos al editor el pinch/pan nativos.
    if (window.matchMedia('(orientation: portrait)').matches) {
      el.style.touchAction = 'none';
    }
  }

  private onMouseDown(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    // En touchstart el evento nativo NO tiene `button` (es TouchEvent),
    // así que hay que normalizar. Por convención un touch se comporta
    // como botón izquierdo (0). El resto de campos exclusivos de mouse
    // (ctrlKey, shiftKey, metaKey) tampoco existen en TouchEvent, se
    // toman como false.
    const isTouch = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent;
    // Un touchstart con más de un dedo lo gestiona setupTouchGestures
    // (pinch-to-zoom): aquí no debemos hacer nada, para no arrancar
    // un drag por accidente.
    if (isTouch && (e.evt as TouchEvent).touches.length >= 2) return;
    const button = isTouch ? 0 : (e.evt as MouseEvent).button;
    const evtMod = isTouch
      ? { ctrlKey: false, metaKey: false, shiftKey: false }
      : (e.evt as MouseEvent);

    // Colocación de CABLE (2 fases):
    //  - floating: primer click fija `from` y pasa a 'growing'.
    //  - growing:  segundo click fija `to` y termina.
    // Colocación de SOLDADURA: un solo click sobre un pin o cable
    // coloca una bola en el vértice más cercano del path (extremo o
    // bend). Click en vacío = no hace nada (para permitir Esc cancelar
    // sin perder el foco).
    if (this.placingSolder) {
      if (button !== 0) return;
      e.evt.preventDefault();
      const hit = this.findHit(e.target);
      const world = this.pointerWorld();
      if (hit && world) this.placeSolderAt(hit, world);
      return;
    }
    if (this.placingWire) {
      if (button !== 0) return;
      e.evt.preventDefault();
      if (this.placingWire.phase === 'floating') {
        // Fijar el origen EN EL PUNTO TOCADO. En escritorio `from` ya
        // venía siguiendo al cursor por onMouseMove, así que esto no
        // cambia nada; en táctil no hay hover, nunca se dispara un
        // move previo y `from` seguiría en el ancla obsoleta con la
        // que se creó el cable (fuera de pantalla) — el primer toque
        // acababa definiendo el FINAL en vez del principio.
        const startWorld = this.pointerWorld();
        const wire = this.findWire(this.placingWire.uid);
        if (startWorld && wire) {
          wire.from = snapPointToGrid(startWorld, this.gridStep);
          wire.direction_deg = this.placingWire.initialDirDeg;
          wire.bend_points_count = 0;
          wire.bends = [];
          wire.cut_at_index = null;
        }
        this.placingWire.phase = 'growing';
        this.rebuild();
        return;
      }
      const uid = this.placingWire.uid;
      this.placingWire = null;
      this.container.style.cursor = '';
      this.commitPendingUndo();
      this.onPlacingWireChange?.(false);
      this.setSelection({ kind: 'wire', wire_uid: uid });
      // La selección ya era este cable desde que empezó la colocación,
      // así que `setSelection` no repinta nada. Hay que forzarlo para
      // que el cable pase de semitransparente a opaco y desaparezca la
      // cruz de colocación.
      this.rebuild();
      return;
    }
    // Colocación de componente: el clic izquierdo fija la posición y sale.
    if (this.placing) {
      if (button !== 0) return;
      e.evt.preventDefault();
      this.confirmPlacement();
      return;
    }

    // Pan (prioridad). En touch nunca es medio ni con Space, siempre
    // es "left".
    const isMiddle = button === 1;
    const isLeftWithSpace = button === 0 && this.spaceDown;
    if (isMiddle || isLeftWithSpace) {
      e.evt.preventDefault();
      this.panning = true;
      this.lastPanPoint = this.stage.getPointerPosition();
      this.container.style.cursor = 'grabbing';
      return;
    }

    if (button !== 0) return;

    const hit = this.findHit(e.target);
    const additive = evtMod.ctrlKey || evtMod.metaKey || evtMod.shiftKey;
    const world = this.pointerWorld();

    // Handles del PIN seleccionado — el tip lleva un círculo naranja
    // decorativo; detectamos click por proximidad para alargar/encoger
    // el pin (respetando su longitud máxima). Debe ir antes del hit
    // ordinario porque el círculo cae encima del propio pin.
    const pinEnd = world ? this.pinEndpointHitFor(world) : null;
    if (pinEnd) {
      e.evt.preventDefault();
      this.startPinResize(pinEnd.componentUid, pinEnd.pinIndex);
      return;
    }

    // Los handles circulares de los extremos del cable seleccionado
    // son PURAMENTE VISUALES (listening=false), pero caen justo encima
    // del último quad del cable, que SÍ escucha. Sin este chequeo el
    // click aterriza en el cable y arranca un drag en vez del resize.
    // Por eso comprobamos proximidad AQUÍ, antes de resolver el hit
    // ordinario — así un click cerca de un extremo del cable ya
    // seleccionado siempre alarga/acorta.
    const endHit = world ? this.endpointHitFor(world) : null;
    if (endHit) {
      e.evt.preventDefault();
      this.startWireResize(endHit.wire_uid, endHit.which);
      return;
    }

    if (!hit) {
      // Click en zona vacía → arranca rubber band; sin arrastre y sin
      // modificador termina limpiando la selección.
      if (world) this.startRubberBand(world, additive);
      return;
    }
    // Click sobre un cable (o su punto de doblez): PRESERVAR la
    // multi-selección si ese cable ya está en ella (para que el
    // drag arrastre todo el bloque). En caso contrario, seleccionar
    // el cable/bend individualmente.
    const wireHit = hit.kind === 'wire' || hit.kind === 'wire-bend' ? hit : null;
    if (wireHit && wireHit.wire_uid) {
      const multiHasWire =
        this.selection?.kind === 'multi' &&
        (this.selection.wire_uids ?? []).includes(wireHit.wire_uid);
      if (!multiHasWire) {
        // Si el cable YA está en modo wire-bend, el tap NO elige un
        // punto de doblez: elige QUÉ LADO del bend actual se va a
        // mover. Se resuelve por geometría pura (ignorando el hit
        // test, que devolvía 'wire-bend' para casi cualquier punto
        // del cable porque los marcadores están cada paso de rejilla):
        //   - tocar el lado NO resaltado  → ese lado pasa a ser el activo.
        //   - tocar el lado YA resaltado  → se mueve el bend point al
        //     vértice más cercano al tap, conservando el lado.
        const cur = this.selection;
        if (
          cur?.kind === 'wire-bend' &&
          cur.wire_uid === wireHit.wire_uid &&
          world
        ) {
          const tappedSide = this.wireSideForWorldPoint(cur.wire_uid, cur.bend_index, world);
          if (tappedSide && tappedSide !== cur.side) {
            this.setSelection({ ...cur, side: tappedSide });
          } else {
            const nearest = this.nearestWireBend(cur.wire_uid, world);
            if (nearest && nearest.bend_index !== cur.bend_index) {
              const solder = this.findSolderAt({
                kind: 'wire',
                wire_uid: cur.wire_uid,
                at: nearest.bend_index,
              });
              if (solder) {
                this.setSelection({ kind: 'solder', solder_uid: solder.uid });
                return;
              }
              this.setSelection({ ...cur, bend_index: nearest.bend_index });
            }
          }
          // Se sigue permitiendo arrastrar el cable desde aquí: si el
          // puntero no llega a moverse, el drag se descarta y sólo
          // queda el cambio de lado / bend.
          this.startWireDrag(wireHit.wire_uid);
          return;
        }

        if (wireHit.kind === 'wire-bend' && typeof wireHit.bend_index === 'number') {
          // Primera vez que entramos en wire-bend sobre este cable.
          const solder = this.findSolderAt({
            kind: 'wire',
            wire_uid: wireHit.wire_uid,
            at: wireHit.bend_index,
          });
          if (solder) {
            this.setSelection({ kind: 'solder', solder_uid: solder.uid });
            return;
          }
          this.setSelection({
            kind: 'wire-bend',
            wire_uid: wireHit.wire_uid,
            bend_index: wireHit.bend_index,
            side: 'right',
          });
        } else if (wireHit.kind === 'wire') {
          this.setSelection({ kind: 'wire', wire_uid: wireHit.wire_uid });
        }
      }
      // En ambos casos (single o multi preservado), iniciar drag:
      // el drag toma los followers a partir de la selección actual.
      this.startWireDrag(wireHit.wire_uid);
      return;
    }
    const currentUids = selectedComponentUids(this.selection);
    const currentSet = new Set(currentUids);

    // Con Ctrl/Cmd/Shift: TOGGLE del componente clicado en el
    // set multi-selección (independiente de qué parte del componente
    // se haya clicado).
    if (additive) {
      e.evt.preventDefault();
      const nextSet = new Set(currentSet);
      if (nextSet.has(hit.component_uid)) nextSet.delete(hit.component_uid);
      else nextSet.add(hit.component_uid);
      const uids = [...nextSet];
      if (uids.length === 0) this.setSelection(null);
      else if (uids.length === 1) this.setSelection({ kind: 'component', component_uid: uids[0]! });
      else this.setSelection({ kind: 'multi', component_uids: uids });
      return;
    }

    // Si el componente clicado YA está en una multi-selección y no
    // hay modificador → mantener el multi e iniciar drag del grupo.
    if (this.selection?.kind === 'multi' && currentSet.has(hit.component_uid) && hit.kind === 'body') {
      this.startDrag(hit.component_uid);
      return;
    }

    const sel = this.selection;
    const currentComp = sel && (
      sel.kind === 'component' || sel.kind === 'pin' || sel.kind === 'bend'
    ) ? sel.component_uid : null;

    if (hit.component_uid !== currentComp) {
      // Primer clic sobre este componente: selecciona el componente.
      this.setSelection({ kind: 'component', component_uid: hit.component_uid });
      if (hit.kind === 'body') {
        this.startDrag(hit.component_uid);
      }
      return;
    }

    // Componente ya seleccionado: se puede drill-down.
    switch (hit.kind) {
      case 'bend':
        if (hit.pin_id !== undefined && hit.bend_index !== undefined) {
          // Si en ese punto de doblez hay una soldadura, ganan las
          // acciones de la soldadura (eliminar borra la bola). Sin
          // soldadura, selección normal del bend.
          const solder = this.findSolderAt({
            kind: 'pin',
            component_uid: hit.component_uid,
            pin_id: hit.pin_id,
            at: hit.bend_index,
          });
          if (solder) {
            this.setSelection({ kind: 'solder', solder_uid: solder.uid });
          } else {
            this.setSelection({
              kind: 'bend',
              component_uid: hit.component_uid,
              pin_id: hit.pin_id,
              bend_index: hit.bend_index,
            });
          }
        }
        break;
      case 'pin':
        if (hit.pin_id !== undefined) {
          this.setSelection({
            kind: 'pin',
            component_uid: hit.component_uid,
            pin_id: hit.pin_id,
          });
        }
        break;
      case 'body':
        // Se queda como componente y admite arrastre.
        this.setSelection({ kind: 'component', component_uid: hit.component_uid });
        this.startDrag(hit.component_uid);
        break;
    }
  }

  private onMouseMove(): void {
    // Redimensionado de un pin o un cable por uno de sus extremos.
    if (this.resizingPin) {
      this.updatePinResize();
      return;
    }
    if (this.resizingWire) {
      this.updateWireResize();
      return;
    }
    // Preview de SOLDADURA: la bola sigue al puntero. Si el puntero
    // está sobre un pin/cable, la bola se snap-ea al vértice más
    // cercano y se muestra con el radio real que tendrá al colocarla.
    if (this.placingSolder && this.solderPreview) {
      const world = this.pointerWorld();
      if (world) {
        this.solderPreview.visible(true);
        // Buscamos qué hay bajo el puntero.
        const hitNode = this.stage.getIntersection(this.stage.getPointerPosition()!);
        const hit = hitNode ? this.findHit(hitNode) : null;
        const snapped = hit ? this.previewSolderAnchor(hit, world) : null;
        if (snapped) {
          this.solderPreview.position({ x: snapped.pos.x, y: snapped.pos.y });
          const ball = this.solderPreview.getChildren()[0] as Konva.Circle | undefined;
          if (ball) ball.radius(snapped.radius);
        } else {
          this.solderPreview.position({ x: world.x, y: world.y });
          const ball = this.solderPreview.getChildren()[0] as Konva.Circle | undefined;
          if (ball) ball.radius(this.gridStep * 0.375);
        }
        this.overlayLayer.batchDraw();
      }
      return;
    }
    // Colocación de CABLE: ambos extremos siguen al cursor en fase
    // 'floating', sólo el extremo libre en fase 'growing'.
    if (this.placingWire) {
      const world = this.pointerWorld();
      if (!world) return;
      const wire = this.findWire(this.placingWire.uid);
      if (!wire) return;
      if (this.placingWire.phase === 'floating') {
        // Cable recto de 1 segmento; se traslada con el cursor.
        wire.from = snapPointToGrid(world, this.gridStep);
        wire.direction_deg = this.placingWire.initialDirDeg;
        wire.bend_points_count = 0;
        wire.bends = [];
        wire.cut_at_index = null;
      } else {
        // Fase 'growing': from fijo; el otro extremo sigue al cursor
        // proyectado sobre una de las 8 direcciones (múltiplos de 45°),
        // longitud en pasos de rejilla.
        const dx = world.x - wire.from.x;
        const dy = world.y - wire.from.y;
        if (dx !== 0 || dy !== 0) {
          const angDeg = (((Math.atan2(dy, dx) * 180) / Math.PI) % 360 + 360) % 360;
          const snappedDeg = Math.round(angDeg / 45) * 45;
          const snappedRad = snappedDeg * Math.PI / 180;
          const isAxial = snappedDeg % 90 === 0;
          const step = isAxial ? this.gridStep : this.gridStep * Math.SQRT2;
          const projected = dx * Math.cos(snappedRad) + dy * Math.sin(snappedRad);
          const nSteps = Math.max(1, Math.round(projected / step));
          wire.direction_deg = snappedDeg;
          wire.bend_points_count = Math.max(0, nSteps - 1);
          // Ajusta longitud del array de bends manteniendo los ya definidos.
          while (wire.bends.length < wire.bend_points_count) {
            wire.bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
          }
          wire.bends.length = wire.bend_points_count;
        }
      }
      this.rebuild();
      return;
    }

    // Modo colocación: el componente ata su posición al cursor.
    if (this.placing) {
      const world = this.pointerWorld();
      if (!world) return;
      const inst = this.findInstance(this.placing.uid);
      if (!inst) return;
      const def = this.library.get(inst.def_id);
      const target = def
        ? this.snapToGridForComponent(inst, def, { x: world.x, y: world.y })
        : { x: world.x, y: world.y };
      inst.position_mm = target;
      this.applyComponentTransform(inst);
      // Followers (duplicado en bloque): siguen al líder con su
      // offset original respecto a él.
      if (this.placing.followUids && this.placing.followOffsets) {
        for (let i = 0; i < this.placing.followUids.length; i++) {
          const fu = this.placing.followUids[i]!;
          const off = this.placing.followOffsets[i]!;
          const finst = this.findInstance(fu);
          if (!finst) continue;
          finst.position_mm = { x: target.x + off.x, y: target.y + off.y };
          this.applyComponentTransform(finst);
        }
      }
      // Cables duplicados: mueven wire.from siguiendo al líder.
      // Al modificar el modelo hace falta reconstruir el layer de wires.
      if (this.placing.followWireUids && this.placing.followWireOffsets) {
        for (let i = 0; i < this.placing.followWireUids.length; i++) {
          const wu = this.placing.followWireUids[i]!;
          const off = this.placing.followWireOffsets[i]!;
          const w = this.findWire(wu);
          if (!w) continue;
          w.from = { x: target.x + off.x, y: target.y + off.y };
        }
        this.rebuild();
      }
      this.drawSelectionOverlay();
      this.worldLayer.batchDraw();
      return;
    }

    // Rubber band: actualiza el rectángulo mientras se arrastra.
    if (this.rubberBand) {
      const world = this.pointerWorld();
      if (!world) return;
      this.rubberBand.moved = true;
      this.rubberBand.lastWorld = { x: world.x, y: world.y };
      const { startWorld } = this.rubberBand;
      const x = Math.min(startWorld.x, world.x);
      const y = Math.min(startWorld.y, world.y);
      const w = Math.abs(world.x - startWorld.x);
      const h = Math.abs(world.y - startWorld.y);
      // Estilo distinto según modo:
      //   toca (LTR):  azul sólido con relleno tenue
      //   contiene (RTL): verde dashado sin relleno
      const containMode = world.x < startWorld.x;
      this.rubberBand.rect.setAttrs({
        x, y, width: w, height: h,
        stroke: containMode ? '#2e7d32' : '#1976d2',
        dash: containMode ? [1.0, 0.6] : [],
        fill: containMode ? 'rgba(46, 125, 50, 0.05)' : 'rgba(33, 150, 243, 0.10)',
      });
      this.overlayLayer.batchDraw();
      return;
    }

    if (this.panning && this.lastPanPoint) {
      const p = this.stage.getPointerPosition();
      if (!p) return;
      const dx = p.x - this.lastPanPoint.x;
      const dy = p.y - this.lastPanPoint.y;
      this.stage.position({ x: this.stage.x() + dx, y: this.stage.y() + dy });
      this.lastPanPoint = p;
      this.drawGrid();
      this.stage.batchDraw();
      return;
    }

    if (this.dragging) {
      const world = this.pointerWorld();
      if (!world) return;
      const dx = world.x - this.dragging.startWorld.x;
      const dy = world.y - this.dragging.startWorld.y;
      // El arrastre sí mueve algo: confirma la foto tomada en el
      // mousedown. Un click sin arrastre nunca llega aquí y no gasta
      // un paso de historial.
      if (dx !== 0 || dy !== 0) this.commitPendingUndo();
      // Rama cable: mueve `wire.from` (todo el cable se traslada).
      if (this.dragging.kind === 'wire') {
        const wire = this.findWire(this.dragging.uid);
        if (!wire) return;
        const snapped = snapPointToGrid(
          { x: this.dragging.startPos.x + dx, y: this.dragging.startPos.y + dy },
          this.gridStep,
        );
        // Delta REAL (post-snap) que aplica el líder — se propaga
        // idéntico a los followers para que el bloque se mueva junto.
        const realDx = snapped.x - this.dragging.startPos.x;
        const realDy = snapped.y - this.dragging.startPos.y;
        wire.from = snapped;
        if (this.dragging.wireFollowers) {
          for (const f of this.dragging.wireFollowers) {
            const other = this.findWire(f.uid);
            if (other) other.from = { x: f.startPos.x + realDx, y: f.startPos.y + realDy };
          }
        }
        if (this.dragging.followers) {
          for (const f of this.dragging.followers) {
            const inst = this.findInstance(f.uid);
            if (inst) inst.position_mm = { x: f.startPos.x + realDx, y: f.startPos.y + realDy };
          }
        }
        this.rebuild();
        return;
      }
      const inst = this.findInstance(this.dragging.uid);
      if (!inst) return;
      const raw = {
        x: this.dragging.startPos.x + dx,
        y: this.dragging.startPos.y + dy,
      };
      const def = this.library.get(inst.def_id);
      const snapped = def ? this.snapToGridForComponent(inst, def, raw) : raw;
      // Delta REAL (post-snap) que aplica el líder.
      const realDx = snapped.x - this.dragging.startPos.x;
      const realDy = snapped.y - this.dragging.startPos.y;
      inst.position_mm = snapped;
      this.applyComponentTransform(inst);
      // Followers: se desplazan por el mismo delta real, sin snap
      // individual (mantienen su offset relativo al líder).
      if (this.dragging.followers) {
        for (const f of this.dragging.followers) {
          const finst = this.findInstance(f.uid);
          if (!finst) continue;
          finst.position_mm = { x: f.startPos.x + realDx, y: f.startPos.y + realDy };
          this.applyComponentTransform(finst);
        }
      }
      // Followers-cable: se traslada `wire.from` con el mismo delta.
      if (this.dragging.wireFollowers) {
        for (const f of this.dragging.wireFollowers) {
          const wire = this.findWire(f.uid);
          if (!wire) continue;
          wire.from = { x: f.startPos.x + realDx, y: f.startPos.y + realDy };
        }
        // Cables no tienen aplicComponentTransform: hacemos rebuild
        // (más caro pero infrecuente en drag).
        this.rebuild();
      } else {
        this.drawSelectionOverlay();
        this.worldLayer.batchDraw();
      }
    }
  }

  private onMouseUp(): void {
    if (this.panning) {
      this.panning = false;
      this.lastPanPoint = null;
      this.container.style.cursor = this.spaceDown ? 'grab' : '';
      return;
    }
    if (this.rubberBand) {
      this.finishRubberBand();
      return;
    }
    if (this.resizingPin) {
      this.resizingPin = null;
      this.container.style.cursor = '';
      this.pendingUndo = null;
      return;
    }
    if (this.resizingWire) {
      this.resizingWire = null;
      this.container.style.cursor = '';
      // Si el redimensionado no llegó a alterar nada, descarta la foto.
      this.pendingUndo = null;
      return;
    }
    if (this.dragging) {
      this.dragging = null;
      this.container.style.cursor = '';
      // Si el arrastre nunca llegó a mover nada, se descarta la foto.
      this.pendingUndo = null;
    }
  }

  /** Comienza el arrastre de un rectángulo de selección. */
  private startRubberBand(world: { x: number; y: number }, additive: boolean): void {
    const rect = new Konva.Rect({
      x: world.x,
      y: world.y,
      width: 0,
      height: 0,
      stroke: '#1976d2',
      strokeWidth: 0.2,
      fill: 'rgba(33, 150, 243, 0.10)',
      listening: false,
    });
    this.overlayLayer.add(rect);
    this.overlayLayer.batchDraw();
    this.rubberBand = {
      startWorld: { x: world.x, y: world.y },
      lastWorld: { x: world.x, y: world.y },
      additive,
      prevSelection: this.selection,
      moved: false,
      rect,
    };
  }

  /** Cierra el rubber band y aplica la selección resultante. */
  private finishRubberBand(): void {
    if (!this.rubberBand || !this.currentProject) return;
    const rb = this.rubberBand;
    this.rubberBand = null;
    // Usamos las coordenadas mm que ya escribimos en el propio rect
    // (evita ambigüedades de getClientRect entre layers distintos).
    const rect = {
      x: rb.rect.x(),
      y: rb.rect.y(),
      width: rb.rect.width(),
      height: rb.rect.height(),
    };
    rb.rect.destroy();
    this.overlayLayer.batchDraw();

    // Si el usuario no llegó a arrastrar, se comporta como click:
    // limpia selección (o mantiene si era additive).
    if (!rb.moved) {
      if (!rb.additive) this.setSelection(null);
      return;
    }

    // Modo: contiene (RTL) o toca (LTR).
    const containMode = rb.lastWorld.x < rb.startWorld.x;
    const hits: Uid[] = [];
    for (const inst of this.currentProject.components) {
      const node = this.findComponentNode(inst.uid);
      if (!node) continue;
      const box = node.getClientRect({ relativeTo: this.componentsGroup });
      const match = containMode ? rectContainsRect(rect, box) : rectsIntersect(rect, box);
      if (match) hits.push(inst.uid);
    }
    // También los cables: bounding box axis-aligned de su path.
    const wireHits: Uid[] = [];
    for (const wire of this.currentProject.wires ?? []) {
      const box = wireBoundingBox(wire);
      const match = containMode ? rectContainsRect(rect, box) : rectsIntersect(rect, box);
      if (match) wireHits.push(wire.uid);
    }

    let finalComps: Uid[];
    let finalWires: Uid[];
    if (rb.additive) {
      const prev = rb.prevSelection;
      const compSet = new Set(selectedComponentUids(prev));
      const wireSet = new Set(selectedWireUids(prev));
      for (const u of hits) compSet.add(u);
      for (const u of wireHits) wireSet.add(u);
      finalComps = [...compSet];
      finalWires = [...wireSet];
    } else {
      finalComps = hits;
      finalWires = wireHits;
    }
    const total = finalComps.length + finalWires.length;
    if (total === 0) this.setSelection(null);
    else if (total === 1 && finalWires.length === 1) {
      this.setSelection({ kind: 'wire', wire_uid: finalWires[0]! });
    } else if (total === 1) {
      this.setSelection({ kind: 'component', component_uid: finalComps[0]! });
    } else {
      this.setSelection({
        kind: 'multi',
        component_uids: finalComps,
        ...(finalWires.length > 0 ? { wire_uids: finalWires } : {}),
      });
    }
  }

  private startDrag(uid: Uid): void {
    const world = this.pointerWorld();
    const inst = this.findInstance(uid);
    if (!world || !inst) return;
    // Si hay multi-selección, los demás componentes/cables seleccionados
    // se convierten en "followers" y se mueven con el mismo delta.
    let followers: { uid: Uid; startPos: { x: number; y: number } }[] | undefined;
    let wireFollowers: { uid: Uid; startPos: { x: number; y: number } }[] | undefined;
    if (this.selection?.kind === 'multi') {
      followers = [];
      for (const otherUid of this.selection.component_uids) {
        if (otherUid === uid) continue;
        const other = this.findInstance(otherUid);
        if (other) followers.push({
          uid: otherUid,
          startPos: { x: other.position_mm.x, y: other.position_mm.y },
        });
      }
      const wUids = this.selection.wire_uids ?? [];
      if (wUids.length > 0) {
        wireFollowers = [];
        for (const wUid of wUids) {
          const wire = this.findWire(wUid);
          if (wire) wireFollowers.push({
            uid: wUid,
            startPos: { x: wire.from.x, y: wire.from.y },
          });
        }
      }
    }
    this.beginPendingUndo();
    this.dragging = {
      kind: 'component',
      uid,
      startWorld: world,
      startPos: { x: inst.position_mm.x, y: inst.position_mm.y },
      ...(followers && followers.length > 0 ? { followers } : {}),
      ...(wireFollowers && wireFollowers.length > 0 ? { wireFollowers } : {}),
    };
    this.container.style.cursor = 'move';
  }

  /**
   * Vuelve a tomar las fotos de referencia de las interacciones en
   * curso (arrastre y colocación/duplicado).
   *
   * Ambas recolocan el bloque en cada mousemove a partir de una foto
   * tomada al empezar: el arrastre usa `startPos + delta`, y la
   * colocación `posiciónDelLíder + followOffsets`. Si mientras tanto el
   * usuario rota o invierte el bloque —mantener pulsado y darle a R, o
   * girar unos clones antes de fijarlos— esas fotos quedan obsoletas y
   * el siguiente movimiento del ratón reconstruye el layout PREVIO a la
   * rotación conservando los `rotation_deg` ya girados: el bloque se
   * abre.
   *
   * Por eso todo mutador de posición/orientación llama aquí al acabar.
   */
  private resyncInteractionBaselines(): void {
    this.resyncPlacingOffsets();
    const d = this.dragging;
    if (!d) return;
    const world = this.pointerWorld();
    if (world) d.startWorld = { x: world.x, y: world.y };
    if (d.kind === 'wire') {
      const w = this.findWire(d.uid);
      if (w) d.startPos = { x: w.from.x, y: w.from.y };
    } else {
      const inst = this.findInstance(d.uid);
      if (inst) d.startPos = { x: inst.position_mm.x, y: inst.position_mm.y };
    }
    for (const f of d.followers ?? []) {
      const inst = this.findInstance(f.uid);
      if (inst) f.startPos = { x: inst.position_mm.x, y: inst.position_mm.y };
    }
    for (const f of d.wireFollowers ?? []) {
      const w = this.findWire(f.uid);
      if (w) f.startPos = { x: w.from.x, y: w.from.y };
    }
  }

  /**
   * Recalcula los offsets de los followers de una colocación en curso
   * (duplicado con Ctrl+C pendiente de fijar) respecto a la posición
   * ACTUAL del líder. Necesario si el bloque se rota o invierte antes
   * de fijarlo: los offsets capturados al duplicar corresponden al
   * layout sin girar.
   */
  private resyncPlacingOffsets(): void {
    const p = this.placing;
    if (!p) return;
    const leader = this.findInstance(p.uid);
    if (!leader) return;
    const lx = leader.position_mm.x;
    const ly = leader.position_mm.y;
    if (p.followUids && p.followOffsets) {
      for (let i = 0; i < p.followUids.length; i++) {
        const inst = this.findInstance(p.followUids[i]!);
        if (inst) {
          p.followOffsets[i] = { x: inst.position_mm.x - lx, y: inst.position_mm.y - ly };
        }
      }
    }
    if (p.followWireUids && p.followWireOffsets) {
      for (let i = 0; i < p.followWireUids.length; i++) {
        const w = this.findWire(p.followWireUids[i]!);
        if (w) {
          p.followWireOffsets[i] = { x: w.from.x - lx, y: w.from.y - ly };
        }
      }
    }
  }

  /**
   * Divide el cable `wireUid` en dos cables independientes por el
   * bend `bendIndex`. El primer cable conserva `from` original y
   * llega hasta la posición del bend (segmentos 0..bendIndex).
   * El segundo cable arranca en la posición del bend con la
   * dirección heredada (azimuth acumulado tras aplicar bend[bendIndex])
   * y usa los bends posteriores. Ninguno queda con corte pendiente.
   */
  private splitWireAt(wireUid: Uid, bendIndex: number): void {
    if (!this.currentProject) return;
    const wire = this.findWire(wireUid);
    if (!wire) return;
    if (bendIndex < 0 || bendIndex >= wire.bend_points_count) return;
    const wp = wireAsPin(wire);
    const path = computePinPath3D(wp.def, wp.state);
    const vertexAtBend = path[bendIndex + 1]; // bend i = path[i+1]
    if (!vertexAtBend) return;

    const wire1: Wire = {
      uid: `w${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`,
      from: { x: wire.from.x, y: wire.from.y },
      direction_deg: wire.direction_deg,
      bend_points_count: bendIndex,
      bends: wire.bends.slice(0, bendIndex).map((b) => ({ ...b })),
      cut_at_index: null,
      z_order: wire.z_order,
    };
    const fwd = vertexAtBend.forward;
    // Snap a múltiplo de 45°: `atan2` devuelve valores como
    // 89.99999999999999 y un `direction_deg` no entero acaba
    // derivando al acumular giros sobre el cable.
    const dirDeg = Math.round((Math.atan2(fwd.y, fwd.x) * 180 / Math.PI) / 45) * 45;
    const wire2: Wire = {
      uid: `w${Date.now().toString(36)}${Math.floor(Math.random() * 1e6 + 1).toString(36)}`,
      from: { x: vertexAtBend.position.x, y: vertexAtBend.position.y },
      direction_deg: dirDeg,
      bend_points_count: Math.max(0, wire.bend_points_count - bendIndex - 1),
      bends: wire.bends.slice(bendIndex + 1).map((b) => ({ ...b })),
      cut_at_index: null,
      z_order: wire.z_order,
    };
    if (!this.currentProject.wires) this.currentProject.wires = [];
    this.currentProject.wires = this.currentProject.wires.filter((w) => w.uid !== wireUid);
    this.currentProject.wires.push(wire1, wire2);
    // Deja seleccionado el primer segmento para dar contexto visual.
    this.setSelection({ kind: 'wire', wire_uid: wire1.uid });
    this.rebuild();
  }

  /** Inicia el arrastre de un cable (mueve `wire.from` con el cursor).
   *  Si hay multi-selección, arrastra en bloque los OTROS cables y
   *  componentes seleccionados con el mismo delta. */
  private startWireDrag(wireUid: Uid): void {
    const world = this.pointerWorld();
    const wire = this.findWire(wireUid);
    if (!world || !wire) return;
    let followers: { uid: Uid; startPos: { x: number; y: number } }[] | undefined;
    let wireFollowers: { uid: Uid; startPos: { x: number; y: number } }[] | undefined;
    if (this.selection?.kind === 'multi') {
      followers = [];
      for (const cUid of this.selection.component_uids) {
        const other = this.findInstance(cUid);
        if (other) followers.push({
          uid: cUid,
          startPos: { x: other.position_mm.x, y: other.position_mm.y },
        });
      }
      const wUids = this.selection.wire_uids ?? [];
      wireFollowers = [];
      for (const wUid of wUids) {
        if (wUid === wireUid) continue;
        const other = this.findWire(wUid);
        if (other) wireFollowers.push({
          uid: wUid,
          startPos: { x: other.from.x, y: other.from.y },
        });
      }
    }
    this.beginPendingUndo();
    this.dragging = {
      kind: 'wire',
      uid: wireUid,
      startWorld: world,
      startPos: { x: wire.from.x, y: wire.from.y },
      ...(followers && followers.length > 0 ? { followers } : {}),
      ...(wireFollowers && wireFollowers.length > 0 ? { wireFollowers } : {}),
    };
    this.container.style.cursor = 'move';
  }

  /**
   * Comienza el redimensionado de un cable por uno de sus extremos.
   * El extremo opuesto queda fijo. Cambia el número de segmentos del
   * cable en pasos enteros, según el desplazamiento del cursor
   * proyectado sobre la dirección del segmento extremo.
   *
   * Sólo se puede acortar más allá de un bend contiguo si ese bend
   * está a 0° (sin curvar). En caso contrario el redimensionado queda
   * limitado al primer segmento del extremo.
   */
  private startWireResize(uid: Uid, which: 'from' | 'tip'): void {
    const wire = this.findWire(uid);
    if (!wire) return;
    // Al agarrar un extremo la intención es SIEMPRE alargar/encoger el
    // cable — un bend cercano que hubiera quedado seleccionado en el
    // click anterior no debe restringir esa acción. Volvemos a un
    // single-wire para que la selección visual (y las condiciones
    // downstream) no confundan al usuario.
    if (this.selection?.kind === 'wire-bend' && this.selection.wire_uid === uid) {
      this.setSelection({ kind: 'wire', wire_uid: uid });
    }
    const wp = wireAsPin(wire);
    const path = computePinPath3D(wp.def, wp.state);
    if (path.length < 2) return;
    // Pivote y "cola" — la cola es el TRAMO RECTO contiguo al extremo
    // agarrado: se extiende desde el extremo hasta el primer bend con
    // ángulo distinto de 0 (o hasta el otro extremo si no hay ninguno).
    // Todos los bends 0 intermedios se pueden consumir al encoger, y
    // al alargar se añaden bends 0 al final. La dirección de la cola
    // (dir) es la real del último tramo — clave para alargar/encoger
    // cables ya doblados siguiendo su nueva dirección.
    let fixed: { x: number; y: number };
    let dir: { x: number; y: number };
    let stepLen: number;
    let initialTailSegments: number;
    if (which === 'tip') {
      // Último bend con ángulo distinto de 0 (o -1 si no hay).
      let lastNonZero = -1;
      for (let i = wire.bends.length - 1; i >= 0; i--) {
        if (wire.bends[i]!.angle_in_plane_deg !== 0) { lastNonZero = i; break; }
      }
      // Vértice del pivote: path[bendIdx + 1] es la posición donde
      // está el bend bendIdx. Sin bends no cero, path[0] = from.
      const pivotVertexIdx = lastNonZero + 1;
      const a = path[pivotVertexIdx]!.position;
      const b = path[path.length - 1]!.position;
      // Dirección + longitud de UN SEGMENTO — no del tramo entero. Sin
      // esto, si la cola tiene N segmentos, stepLen valdría N × 2.54 y
      // arrastrar un solo paso ya no cambiaría el bend_points_count.
      const seg0 = path[path.length - 2]!.position;
      const seg1 = path[path.length - 1]!.position;
      stepLen = Math.hypot(seg1.x - seg0.x, seg1.y - seg0.y) || 1;
      const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      dir = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
      fixed = { x: a.x, y: a.y };
      initialTailSegments = (wire.bend_points_count + 1) - pivotVertexIdx;
    } else {
      // Primer bend con ángulo distinto de 0 (o -1 si no hay).
      let firstNonZero = -1;
      for (let i = 0; i < wire.bends.length; i++) {
        if (wire.bends[i]!.angle_in_plane_deg !== 0) { firstNonZero = i; break; }
      }
      // Sin bends no cero, el pivote es el tip (path[N+1]); si hay,
      // el pivote es el vértice donde está firstNonZero.
      const pivotVertexIdx = firstNonZero === -1 ? path.length - 1 : firstNonZero + 1;
      const a = path[pivotVertexIdx]!.position;
      const b = path[0]!.position;
      // stepLen = longitud del PRIMER segmento del cable (el más
      // cercano al extremo from), NO del tramo entero.
      const seg0 = path[0]!.position;
      const seg1 = path[1]!.position;
      stepLen = Math.hypot(seg1.x - seg0.x, seg1.y - seg0.y) || 1;
      const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      dir = { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
      fixed = { x: a.x, y: a.y };
      initialTailSegments = pivotVertexIdx;
    }
    this.beginPendingUndo();
    // Cursor mundo al arrancar el resize — referencia para medir el
    // DELTA de arrastre. Puede ser null si el pointer no está sobre
    // el canvas (raro en un mousedown legítimo); en ese caso caemos
    // al propio extremo, que garantiza delta 0 al primer mousemove.
    const startWorld = this.pointerWorld() ?? {
      x: which === 'tip' ? fixed.x + dir.x * initialTailSegments * stepLen : fixed.x - dir.x * initialTailSegments * stepLen,
      y: which === 'tip' ? fixed.y + dir.y * initialTailSegments * stepLen : fixed.y - dir.y * initialTailSegments * stepLen,
    };
    this.resizingWire = {
      uid,
      which,
      fixed,
      dir,
      stepLen,
      initialCount: wire.bend_points_count,
      trimIndex: initialTailSegments,
      startWorld,
      initialBends: wire.bends.map((b) => ({ ...b })),
      initialFrom: { x: wire.from.x, y: wire.from.y },
      initialDir: wire.direction_deg,
      lastCount: wire.bend_points_count,
    };
    this.container.style.cursor = 'ew-resize';
  }

  /** Actualiza el cable en curso de redimensionado a partir del cursor. */
  private updateWireResize(): void {
    const r = this.resizingWire;
    if (!r) return;
    const world = this.pointerWorld();
    if (!world) return;
    const wire = this.findWire(r.uid);
    if (!wire) return;
    // Delta del ARRASTRE (no proyección absoluta): número de pasos que
    // el cursor se ha desplazado sobre la dirección de la cola desde
    // el punto de mousedown. Así, al arrancar el resize sin mover el
    // ratón, deltaSteps = 0 y el cable se queda exacto. Antes lo
    // calculaba como proyección absoluta desde el pivote y un jitter
    // mínimo bastaba para colapsar el cable a 1 segmento.
    const relX = world.x - r.startWorld.x;
    const relY = world.y - r.startWorld.y;
    const projDelta = relX * r.dir.x + relY * r.dir.y; // en mm
    const deltaSteps = Math.round(projDelta / r.stepLen);
    // Segmentos en la cola tras aplicar el delta. Mínimo 1: siempre
    // queda al menos un tramo por el lado agarrado.
    const initialTail = r.trimIndex;
    const tailSegments = Math.max(1, initialTail + deltaSteps);
    const delta = tailSegments - initialTail;
    const newCount = r.initialCount + delta;
    if (newCount === r.lastCount) return; // sin cambios
    r.lastCount = newCount;

    if (r.which === 'tip') {
      // Extremo TIP: from y direction se conservan; añadimos/quitamos
      // bends ceros al FINAL para alargar/acortar el último tramo.
      wire.from = { x: r.initialFrom.x, y: r.initialFrom.y };
      wire.direction_deg = r.initialDir;
      wire.bend_points_count = newCount;
      const bends: { angle_in_plane_deg: number; angle_out_of_plane_deg: number }[] = [];
      for (let i = 0; i < newCount; i++) {
        if (i < r.initialBends.length) bends.push({ ...r.initialBends[i]! });
        else bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
      }
      wire.bends = bends;
    } else {
      // Extremo FROM: mantenemos la dirección inicial del cable;
      // añadimos/quitamos bends ceros al PRINCIPIO y movemos `from`
      // hacia atrás/adelante por la dirección inicial. `delta` > 0
      // significa alargar (from se aleja del pivote); < 0 acortar.
      wire.direction_deg = r.initialDir;
      // Dirección "hacia adentro" del cable (from → primer bend), en
      // coord mundo, obtenida de `direction_deg` inicial.
      const rad = r.initialDir * Math.PI / 180;
      const inwardX = Math.cos(rad);
      const inwardY = Math.sin(rad);
      // El primer bend original está en `initialFrom + stepLen·inward`.
      // Para añadir K segmentos delante, from = pivote_original − (initialTail + K) · stepLen · inward.
      // Como pivote_original = initialFrom + initialTail·stepLen·inward:
      wire.from = {
        x: r.initialFrom.x - delta * r.stepLen * inwardX,
        y: r.initialFrom.y - delta * r.stepLen * inwardY,
      };
      wire.bend_points_count = newCount;
      const bends: { angle_in_plane_deg: number; angle_out_of_plane_deg: number }[] = [];
      if (delta >= 0) {
        // K bends ceros al inicio.
        for (let k = 0; k < delta; k++) bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
        for (const b of r.initialBends) bends.push({ ...b });
      } else {
        // Eliminamos |delta| bends del inicio.
        for (let i = -delta; i < r.initialBends.length; i++) bends.push({ ...r.initialBends[i]! });
      }
      while (bends.length < newCount) {
        bends.push({ angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 });
      }
      bends.length = wire.bend_points_count;
      wire.bends = bends;
    }
    // El drag SÍ modifica algo: confirma la foto tomada al empezar.
    this.commitPendingUndo();
    this.rebuild();
  }

  /**
   * Detección por proximidad de un click en el TIP del pin
   * seleccionado (`kind: 'pin'` o `'bend'`). El handle se pinta como
   * decoración en drawSelectionOverlay; aquí decidimos si el click cae
   * lo bastante cerca. Devuelve null si no hay pin seleccionado, si el
   * pin es locked, o si el click está lejos.
   */
  private pinEndpointHitFor(world: { x: number; y: number }): { componentUid: Uid; pinIndex: number } | null {
    const sel = this.selection;
    if (!sel || (sel.kind !== 'pin' && sel.kind !== 'bend')) return null;
    const inst = this.findInstance(sel.component_uid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) return null;
    const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
    if (pinIndex < 0) return null;
    const pinDef = def.pins[pinIndex]!;
    if (pinDef.locked) return null;
    const pinState = inst.pins[pinIndex];
    if (!pinState) return null;
    const path = computePinPath3D(pinDef, pinState);
    if (path.length < 2) return null;
    const tipLocal = path[path.length - 1]!.position;
    const tipWorld = projectLocalToScene(
      tipLocal, inst.position_mm, inst.rotation_deg, inst.flipped ?? false,
    );
    const HANDLE_HIT_RADIUS = 1.4;
    const dx = world.x - tipWorld.x, dy = world.y - tipWorld.y;
    if (dx * dx + dy * dy > HANDLE_HIT_RADIUS * HANDLE_HIT_RADIUS) return null;
    return { componentUid: sel.component_uid, pinIndex };
  }

  /**
   * Comienza a redimensionar el pin agarrado por su tip. Deja la
   * selección en `{kind:'pin'}` (limpia el bend seleccionado si lo
   * había) para que el resize no dependa de qué bend estaba activo.
   */
  private startPinResize(componentUid: Uid, pinIndex: number): void {
    const inst = this.findInstance(componentUid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) return;
    const pinDef = def.pins[pinIndex];
    const pinState = inst.pins[pinIndex];
    if (!pinDef || !pinState || pinDef.locked) return;

    const path = computePinPath3D(pinDef, pinState);
    if (path.length < 2) return;

    // Segmentos actualmente visibles del pin (cut define el máximo
    // interno). Máximo absoluto = bend_points_count + 1.
    const currentSegments =
      pinState.cut_at_index === null ? pinDef.bend_points_count + 1 : pinState.cut_at_index + 1;
    const activeBends =
      pinState.cut_at_index === null ? pinDef.bend_points_count : pinState.cut_at_index;

    // Último bend con ángulo != 0 dentro de los activos.
    let lastNonZero = -1;
    for (let i = activeBends - 1; i >= 0; i--) {
      if ((pinState.bends[i]?.angle_in_plane_deg ?? 0) !== 0) { lastNonZero = i; break; }
    }
    const pivotVertexIdx = lastNonZero + 1;

    // Pivote y tip en LOCAL → transformar a WORLD; también el vértice
    // previo al tip para medir la longitud REAL de un segmento en world.
    const flipped = inst.flipped ?? false;
    const pivotLocal = path[pivotVertexIdx]!.position;
    const tipLocal = path[path.length - 1]!.position;
    const preTipLocal = path[path.length - 2]!.position;
    const pivotWorld = projectLocalToScene(pivotLocal, inst.position_mm, inst.rotation_deg, flipped);
    const tipWorld = projectLocalToScene(tipLocal, inst.position_mm, inst.rotation_deg, flipped);
    const preTipWorld = projectLocalToScene(preTipLocal, inst.position_mm, inst.rotation_deg, flipped);

    const stepLen = Math.hypot(tipWorld.x - preTipWorld.x, tipWorld.y - preTipWorld.y) || 1;
    const l = Math.hypot(tipWorld.x - pivotWorld.x, tipWorld.y - pivotWorld.y) || 1;
    const dir = { x: (tipWorld.x - pivotWorld.x) / l, y: (tipWorld.y - pivotWorld.y) / l };

    // Si veníamos con un bend seleccionado, dejamos la selección en pin
    // para que el usuario no vea el bend "atado".
    if (this.selection?.kind === 'bend'
        && this.selection.component_uid === componentUid
        && this.selection.pin_id === pinDef.id) {
      this.setSelection({ kind: 'pin', component_uid: componentUid, pin_id: pinDef.id });
    }

    const startWorld = this.pointerWorld() ?? tipWorld;
    this.beginPendingUndo();
    this.resizingPin = {
      componentUid, pinIndex,
      fixed: pivotWorld, dir, stepLen, startWorld,
      initialTail: currentSegments - pivotVertexIdx,
      pivotVertexIdx,
      maxTotalSegments: pinDef.bend_points_count + 1,
      lastCut: pinState.cut_at_index,
    };
    this.container.style.cursor = 'ew-resize';
  }

  /** Actualiza el corte del pin según el arrastre del cursor. */
  private updatePinResize(): void {
    const r = this.resizingPin;
    if (!r) return;
    const world = this.pointerWorld();
    if (!world) return;
    const inst = this.findInstance(r.componentUid);
    if (!inst) return;
    const pinState = inst.pins[r.pinIndex];
    if (!pinState) return;

    const relX = world.x - r.startWorld.x;
    const relY = world.y - r.startWorld.y;
    const projDelta = relX * r.dir.x + relY * r.dir.y;
    const deltaSteps = Math.round(projDelta / r.stepLen);

    // Cola nueva: acotada entre 1 (no colapsar) y (max - pivote), es
    // decir el máximo permitido por la longitud original del pin.
    const maxTail = r.maxTotalSegments - r.pivotVertexIdx;
    const tailSegments = Math.max(1, Math.min(maxTail, r.initialTail + deltaSteps));
    const newTotal = r.pivotVertexIdx + tailSegments;
    const newCut = newTotal >= r.maxTotalSegments ? null : newTotal - 1;
    if (newCut === r.lastCut) return;
    r.lastCut = newCut;
    pinState.cut_at_index = newCut;
    this.commitPendingUndo();
    this.rebuild();
  }

  /**
   * Detección por proximidad de un click en los extremos de un cable
   * SELECCIONADO. Los handles se pintan como decoración non-listening,
   * así que un click "cercano" cae aquí. Retorna el extremo si el
   * click está dentro de HANDLE_HIT_RADIUS (en mm) de alguno; null si
   * no o si no hay cables seleccionados.
   */
  private endpointHitFor(world: { x: number; y: number }): { wire_uid: Uid; which: 'from' | 'tip' } | null {
    const wireUids = selectedWireUids(this.selection);
    if (wireUids.length === 0) return null;
    // Radio en mm. 1.4 mm es un poco mayor que el handle visible
    // (0.9 mm de radio) para que sea fácil agarrarlo, pero no tanto
    // como para robar clicks a la zona vacía cercana.
    const HANDLE_HIT_RADIUS = 1.4;
    const r2 = HANDLE_HIT_RADIUS * HANDLE_HIT_RADIUS;
    for (const uid of wireUids) {
      const wire = this.findWire(uid);
      if (!wire) continue;
      const wp = wireAsPin(wire);
      const path = computePinPath3D(wp.def, wp.state);
      if (path.length < 2) continue;
      const from = path[0]!.position;
      const tip = path[path.length - 1]!.position;
      const dxF = world.x - from.x, dyF = world.y - from.y;
      const dxT = world.x - tip.x, dyT = world.y - tip.y;
      const dF = dxF * dxF + dyF * dyF;
      const dT = dxT * dxT + dyT * dyT;
      const minD = Math.min(dF, dT);
      if (minD <= r2) {
        return { wire_uid: uid, which: dF <= dT ? 'from' : 'tip' };
      }
    }
    return null;
  }

  private findHit(target: Konva.Node): HitInfo | null {
    let node: Konva.Node | null = target;
    let kind: 'body' | 'pin' | 'bend' | 'wire' | undefined;
    let pinId: string | undefined;
    let bendIndex: number | undefined;
    while (node && node !== this.stage) {
      if (kind === undefined) {
        const bIdx = node.getAttr('bendIndex') as number | undefined;
        const nk = node.getAttr('nodeKind') as string | undefined;
        if (nk === 'bend' && typeof bIdx === 'number') {
          kind = 'bend';
          bendIndex = bIdx;
        } else if (nk === 'pin') {
          kind = 'pin';
        } else if (nk === 'body') {
          kind = 'body';
        } else if (nk === 'wire') {
          kind = 'wire';
        }
      }
      if (pinId === undefined) {
        const pId = node.getAttr('pinId') as string | undefined;
        if (pId) pinId = pId;
      }
      // Un cable NO tiene componentUid; tiene wireUid. Si además el
      // nodo lleva bendIndex es una marca de doblez del cable. Los
      // handles de extremo son puramente visuales (listening=false),
      // así que aquí NO aparecen — se detectan por proximidad en
      // `endpointHitFor` desde onMouseDown.
      const wUid = node.getAttr('wireUid') as Uid | undefined;
      if (wUid) {
        if (kind === 'bend' && typeof bendIndex === 'number') {
          return { component_uid: '', kind: 'wire-bend', wire_uid: wUid, bend_index: bendIndex };
        }
        return { component_uid: '', kind: 'wire', wire_uid: wUid };
      }
      const uid = node.getAttr('componentUid') as Uid | undefined;
      if (uid) {
        return {
          component_uid: uid,
          kind: kind ?? 'body',
          ...(pinId !== undefined ? { pin_id: pinId } : {}),
          ...(bendIndex !== undefined ? { bend_index: bendIndex } : {}),
        };
      }
      node = node.getParent();
    }
    return null;
  }

  private findInstance(uid: Uid): ComponentInstance | undefined {
    return this.currentProject?.components.find((c) => c.uid === uid);
  }

  private findWire(uid: Uid): Wire | undefined {
    return this.currentProject?.wires?.find((w) => w.uid === uid);
  }

  private findSolder(uid: Uid) {
    return this.currentProject?.solder_joints?.find((s) => s.uid === uid);
  }

  /** Devuelve la soldadura anclada a un punto concreto de un pin o
   *  cable, o undefined si no hay ninguna. */
  private findSolderAt(query: SolderAnchor) {
    const list = this.currentProject?.solder_joints ?? [];
    return list.find((s) => sameAnchor(s.anchor, query));
  }

  private findComponentNode(uid: Uid): Konva.Group | null {
    return (
      (this.componentsGroup.findOne((n: Konva.Node) => n.getAttr('componentUid') === uid) as Konva.Group | undefined) ??
      null
    );
  }

  private applyComponentTransform(inst: ComponentInstance): void {
    const node = this.findComponentNode(inst.uid);
    if (!node) return;
    node.position({ x: inst.position_mm.x, y: inst.position_mm.y });
    node.rotation(inst.rotation_deg);
  }

  private pointerWorld(): { x: number; y: number } | null {
    const p = this.stage.getPointerPosition();
    if (!p) return null;
    const scale = this.stage.scaleX();
    return {
      x: (p.x - this.stage.x()) / scale,
      y: (p.y - this.stage.y()) / scale,
    };
  }

  private drawSelectionOverlay(): void {
    this.overlayLayer.destroyChildren();
    // Colocando un cable: cruz sobre el punto que quedará fijado al
    // hacer click. En fase 'floating' marca dónde EMPIEZA el cable;
    // en 'growing', dónde acaba. Se dibuja antes del early-return de
    // "sin selección" porque debe verse en ambos casos.
    if (this.placingWire) {
      const wire = this.findWire(this.placingWire.uid);
      if (wire) {
        const anchor =
          this.placingWire.phase === 'floating'
            ? { x: wire.from.x, y: wire.from.y }
            : this.wireTipPoint(wire);
        if (anchor) this.drawCrosshair(anchor);
      }
    }
    const sel = this.selection;
    if (!sel || !this.currentProject) {
      this.overlayLayer.batchDraw();
      return;
    }

    // Soldadura seleccionada: anillo naranja alrededor de la bola.
    if (sel.kind === 'solder') {
      const solder = this.findSolder(sel.solder_uid);
      if (!solder) { this.overlayLayer.batchDraw(); return; }
      const pos = this.resolveSolderAnchor(solder.anchor);
      if (!pos) { this.overlayLayer.batchDraw(); return; }
      const r = solder.diameter_mm !== null && solder.diameter_mm !== undefined
        ? solder.diameter_mm / 2
        : this.defaultSolderRadius(solder.anchor);
      this.overlayLayer.add(new Konva.Circle({
        x: pos.x,
        y: pos.y,
        radius: r + 0.25,
        stroke: '#ef6c00',
        strokeWidth: 0.25,
        fill: 'rgba(255, 152, 0, 0.30)',
        listening: false,
      }));
      this.overlayLayer.batchDraw();
      return;
    }

    // Cable (o wire-bend) seleccionado: halo tipo tira ancha por
    // encima del cable, siguiendo su path completa.
    if (sel.kind === 'wire' || sel.kind === 'wire-bend') {
      const wire = this.findWire(sel.wire_uid);
      if (wire) {
        const wp = wireAsPin(wire);
        const path = computePinPath3D(wp.def, wp.state);
        const allPts: number[] = [];
        for (const v of path) allPts.push(v.position.x, v.position.y);
        // Halo base sobre el cable completo.
        if (allPts.length >= 4) {
          this.overlayLayer.add(new Konva.Line({
            points: allPts,
            stroke: '#2196f3',
            strokeWidth: 1.2,
            opacity: 0.35,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
          }));
        }
        if (sel.kind === 'wire-bend') {
          // Sobre-imprimir el LADO ACTIVO en naranja fuerte. Al
          // doblar, este lado es el que se moverá; el otro queda
          // fijo. El usuario cambia el lado tocando en la mitad
          // opuesta del cable.
          const splitAt = path.findIndex((x) => x.bend_index === sel.bend_index);
          if (splitAt > 0 && splitAt < path.length - 1) {
            const from = sel.side === 'left' ? 0 : splitAt;
            const to = sel.side === 'left' ? splitAt : path.length - 1;
            const sidePts: number[] = [];
            for (let i = from; i <= to; i++) {
              sidePts.push(path[i]!.position.x, path[i]!.position.y);
            }
            if (sidePts.length >= 4) {
              this.overlayLayer.add(new Konva.Line({
                points: sidePts,
                stroke: '#f5a24a',
                strokeWidth: 1.2,
                opacity: 0.55,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
              }));
            }
          }
          const v = path.find((x) => x.bend_index === sel.bend_index);
          if (v) {
            this.overlayLayer.add(new Konva.Circle({
              x: v.position.x,
              y: v.position.y,
              radius: 0.8,
              stroke: '#ef6c00',
              strokeWidth: 0.25,
              fill: 'rgba(255, 152, 0, 0.35)',
              listening: false,
            }));
          }
        }
      }
      this.overlayLayer.batchDraw();
      return;
    }

    // Halo por CADA componente en la selección.
    const uids = selectedComponentUids(sel);
    let unionBox: { x: number; y: number; width: number; height: number } | null = null;
    for (const uid of uids) {
      const node = this.findComponentNode(uid);
      if (!node) continue;
      const box = node.getClientRect({ relativeTo: this.worldLayer });
      const margin = 0.5;
      this.overlayLayer.add(
        new Konva.Rect({
          x: box.x - margin,
          y: box.y - margin,
          width: box.width + margin * 2,
          height: box.height + margin * 2,
          stroke: '#2196f3',
          strokeWidth: 0.25,
          dash: [0.8, 0.4],
          listening: false,
        }),
      );
      unionBox = unionBox
        ? {
            x: Math.min(unionBox.x, box.x),
            y: Math.min(unionBox.y, box.y),
            width:
              Math.max(unionBox.x + unionBox.width, box.x + box.width) -
              Math.min(unionBox.x, box.x),
            height:
              Math.max(unionBox.y + unionBox.height, box.y + box.height) -
              Math.min(unionBox.y, box.y),
          }
        : { x: box.x, y: box.y, width: box.width, height: box.height };
    }

    // Halo por cada CABLE en multi-selección.
    if (sel.kind === 'multi' && sel.wire_uids) {
      for (const wUid of sel.wire_uids) {
        const wire = this.findWire(wUid);
        if (!wire) continue;
        // Halo brillante siguiendo la path del cable (mismo estilo
        // que en selección simple para que se identifique como
        // "cable seleccionado", no sólo una caja envolvente).
        const wp = wireAsPin(wire);
        const path = computePinPath3D(wp.def, wp.state);
        const pts: number[] = [];
        for (const v of path) pts.push(v.position.x, v.position.y);
        if (pts.length >= 4) {
          this.overlayLayer.add(new Konva.Line({
            points: pts,
            stroke: '#2196f3',
            strokeWidth: 1.2,
            opacity: 0.35,
            lineCap: 'round',
            lineJoin: 'round',
            listening: false,
          }));
        }
        const box = wireBoundingBox(wire);
        const margin = 0.5;
        this.overlayLayer.add(new Konva.Rect({
          x: box.x - margin,
          y: box.y - margin,
          width: box.width + margin * 2,
          height: box.height + margin * 2,
          stroke: '#2196f3',
          strokeWidth: 0.25,
          dash: [0.8, 0.4],
          listening: false,
        }));
        unionBox = unionBox
          ? {
              x: Math.min(unionBox.x, box.x),
              y: Math.min(unionBox.y, box.y),
              width:
                Math.max(unionBox.x + unionBox.width, box.x + box.width) -
                Math.min(unionBox.x, box.x),
              height:
                Math.max(unionBox.y + unionBox.height, box.y + box.height) -
                Math.min(unionBox.y, box.y),
            }
          : { x: box.x, y: box.y, width: box.width, height: box.height };
      }
    }

    // En multi-selección: bounding box del conjunto en tono más claro
    // y más marcado con dash, para dar la sensación de "grupo".
    if (sel.kind === 'multi' && unionBox) {
      const pad = 1.5;
      this.overlayLayer.add(
        new Konva.Rect({
          x: unionBox.x - pad,
          y: unionBox.y - pad,
          width: unionBox.width + pad * 2,
          height: unionBox.height + pad * 2,
          stroke: '#1976d2',
          strokeWidth: 0.35,
          dash: [1.5, 1.0],
          opacity: 0.7,
          listening: false,
        }),
      );
      this.overlayLayer.batchDraw();
      return;
    }

    // Para selecciones no-multi (y no-wire), seguimos con detalle
    // de pin/bend del componente.
    if (sel.kind === 'multi') {
      this.overlayLayer.batchDraw();
      return;
    }
    const inst = this.findInstance(sel.component_uid);
    const def = inst ? this.library.get(inst.def_id) : undefined;
    if (!inst || !def) {
      this.overlayLayer.batchDraw();
      return;
    }

    // Halo específico de pin / bend.
    if (sel.kind === 'pin' || sel.kind === 'bend') {
      const pinIndex = def.pins.findIndex((p) => p.id === sel.pin_id);
      const pinDef = pinIndex >= 0 ? def.pins[pinIndex] : undefined;
      const pinState = pinIndex >= 0 ? inst.pins[pinIndex] : undefined;
      if (pinDef && pinState) {
        const path = computePinPath3D(pinDef, pinState);
        const pts: number[] = [];
        for (const v of path) {
          const p = projectLocalToScene(
            v.position,
            inst.position_mm,
            inst.rotation_deg,
            inst.flipped ?? false,
          );
          pts.push(p.x, p.y);
        }
        if (pts.length >= 4) {
          this.overlayLayer.add(
            new Konva.Line({
              points: pts,
              stroke: '#ff9800',
              strokeWidth: pinDef.diameter_mm * 1.6,
              opacity: 0.35,
              lineCap: 'round',
              lineJoin: 'round',
              listening: false,
            }),
          );
        }
        if (sel.kind === 'bend') {
          const v = path.find((x) => x.bend_index === sel.bend_index);
          if (v) {
            const p = projectLocalToScene(v.position, inst.position_mm, inst.rotation_deg, inst.flipped ?? false);
            this.overlayLayer.add(
              new Konva.Circle({
                x: p.x,
                y: p.y,
                // Tamaño unificado del círculo de selección de bend
                // (referencia: LED, diameter_mm = 0.55 → 0.55·1.7 ≈ 0.94).
                radius: 0.94,
                stroke: '#ef6c00',
                strokeWidth: 0.25,
                fill: 'rgba(255, 152, 0, 0.35)',
                listening: false,
              }),
            );
          }
        }
        // Handle en el TIP del pin — permite tirar del extremo para
        // alargar / encoger el pin (`pinEndpointHitFor` detecta el
        // click por proximidad, el handle es sólo decoración).
        if (!pinDef.locked && path.length >= 2) {
          const tipLocal = path[path.length - 1]!.position;
          const tipWorld = projectLocalToScene(
            tipLocal, inst.position_mm, inst.rotation_deg, inst.flipped ?? false,
          );
          this.overlayLayer.add(new Konva.Circle({
            x: tipWorld.x, y: tipWorld.y,
            radius: 0.9,
            fill: '#ffffff',
            stroke: '#d9700f',
            strokeWidth: 0.25,
            listening: false,
          }));
        }
      }
    }

    this.overlayLayer.batchDraw();
  }

}

/**
 * Traduce el ancla de una soldadura al clon correspondiente cuando el
 * elemento al que apunta acaba de duplicarse. Devuelve null si el
 * ancla apunta a algo que NO forma parte del bloque duplicado (esa
 * soldadura no debe clonarse).
 */
function remapSolderAnchor(
  a: SolderAnchor,
  compUidMap: ReadonlyMap<Uid, Uid>,
  wireUidMap: ReadonlyMap<Uid, Uid>,
): SolderAnchor | null {
  if (a.kind === 'pin') {
    const clone = compUidMap.get(a.component_uid);
    if (!clone) return null;
    return { kind: 'pin', component_uid: clone, pin_id: a.pin_id, at: a.at };
  }
  const clone = wireUidMap.get(a.wire_uid);
  if (!clone) return null;
  return { kind: 'wire', wire_uid: clone, at: a.at };
}

/** Igualdad estructural entre dos anclas de soldadura. */
function sameAnchor(a: SolderAnchor, b: SolderAnchor): boolean {
  if (a.kind !== b.kind) return false;
  if (a.at !== b.at) return false;
  if (a.kind === 'pin' && b.kind === 'pin') {
    return a.component_uid === b.component_uid && a.pin_id === b.pin_id;
  }
  if (a.kind === 'wire' && b.kind === 'wire') {
    return a.wire_uid === b.wire_uid;
  }
  return false;
}

/** Vértice más cercano a un punto de referencia. */
function nearestVertex<T extends { pos: { x: number; y: number } }>(
  vertices: readonly T[],
  ref: { x: number; y: number },
): T | null {
  let best: T | null = null;
  let bestD = Infinity;
  for (const v of vertices) {
    const dx = v.pos.x - ref.x;
    const dy = v.pos.y - ref.y;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = v; }
  }
  return best;
}

/** Devuelve el vértice del path que corresponde al ancla dada. */
function pickVertex(
  path: readonly { position: { x: number; y: number }; bend_index: number | 'attachment' | 'tip' }[],
  at: SolderAnchorAt,
): { position: { x: number; y: number } } | null {
  for (const v of path) {
    if (v.bend_index === at) return v;
  }
  return null;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Distancia euclídea de un punto al segmento [a, b] en 2D. Usa la
 * proyección clamped al intervalo [0, 1] para tratar correctamente
 * los extremos (si la proyección cae fuera del segmento, la distancia
 * es la del extremo más cercano).
 */
function distancePointToSegment(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const qx = a.x + t * dx;
  const qy = a.y + t * dy;
  return Math.hypot(p.x - qx, p.y - qy);
}

function normalizeBodyRotation(angle: number): BodyRotation {
  let a = ((angle % 360) + 360) % 360;
  if (a > 180) a -= 360;
  a = Math.round(a / 90) * 90;
  if (a === -0) a = 0;
  return a as BodyRotation;
}

/**
 * Rota un vector un múltiplo de 90° SIN coma flotante: sólo permuta y
 * niega coordenadas. Convención Konva (Y-down), igual que `Math.atan2`:
 * +90° gira en sentido horario en pantalla → (x, y) ⇒ (−y, x).
 *
 * Usar esto en vez de `cos`/`sin` es lo que garantiza que girar un
 * bloque 4 veces lo deje EXACTAMENTE como estaba: `Math.cos(90°)` vale
 * 6.12e-17, no 0, y ese residuo se acumula vuelta a vuelta.
 */
function rotateVec90(v: { x: number; y: number }, deg: number): { x: number; y: number } {
  const k = ((Math.round(deg / 90) % 4) + 4) % 4;
  switch (k) {
    case 1: return { x: -v.y, y: v.x };
    case 2: return { x: -v.x, y: -v.y };
    case 3: return { x: v.y, y: -v.x };
    default: return { x: v.x, y: v.y };
  }
}

/**
 * Redondea una coordenada a precisión de micra. Sin esto, el residuo de
 * coma flotante de cada giro (del orden de 1e-15 mm) se acumula y el
 * bloque nunca vuelve a un estado exactamente idéntico al girar en
 * bucle. Con el redondeo, 4 giros son la identidad exacta.
 */
function roundMm(v: number): number {
  return Math.round(v * 1e6) / 1e6;
}

/** Snap de un punto al nodo de rejilla más cercano. */
function snapPointToGrid(p: { x: number; y: number }, step: number): { x: number; y: number } {
  return {
    x: Math.round(p.x / step) * step,
    y: Math.round(p.y / step) * step,
  };
}

/** Test de intersección entre dos rectángulos (formato {x,y,width,height}). */
function rectsIntersect(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/** True si el rectángulo `outer` contiene TOTALMENTE al `inner`. */
function rectContainsRect(
  outer: { x: number; y: number; width: number; height: number },
  inner: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

/**
 * De un conjunto de anclas, la que queda más cerca del centro. Es el
 * pivote de las transformaciones de bloque (giro y espejo): el único
 * elemento que se ajusta a rejilla, y el punto fijo de la operación.
 *
 * La elección es estable entre operaciones porque giro y espejo
 * conservan las distancias, así que el mismo elemento sigue siendo el
 * más central — por eso repetir la operación devuelve el bloque a su
 * estado original en vez de ir derivando.
 */
function mostCentralRef(
  refs: readonly { x: number; y: number }[],
): { x: number; y: number } {
  let mx = 0;
  let my = 0;
  for (const r of refs) { mx += r.x; my += r.y; }
  mx /= refs.length;
  my /= refs.length;
  let best = refs[0]!;
  let bestD = (best.x - mx) ** 2 + (best.y - my) ** 2;
  for (let i = 1; i < refs.length; i++) {
    const d = (refs[i]!.x - mx) ** 2 + (refs[i]!.y - my) ** 2;
    if (d < bestD) { bestD = d; best = refs[i]!; }
  }
  return best;
}

/** Clona profundamente una ComponentInstance con nuevo uid, posición y z. */
function cloneInstance(src: ComponentInstance, pos: { x: number; y: number }, z: number): ComponentInstance {
  return {
    uid: `u${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`,
    def_id: src.def_id,
    position_mm: { x: pos.x, y: pos.y },
    rotation_deg: src.rotation_deg,
    z_order: z,
    ...(src.flipped !== undefined ? { flipped: src.flipped } : {}),
    ...(src.value !== undefined ? { value: src.value } : {}),
    ...(src.label !== undefined ? { label: src.label } : {}),
    pins: src.pins.map((p) => ({
      pin_id: p.pin_id,
      bends: p.bends.map((b) => ({ ...b })),
      cut_at_index: p.cut_at_index,
      ...(p.length_adjust_mm !== undefined ? { length_adjust_mm: p.length_adjust_mm } : {}),
      ...(p.angle_adjust_deg !== undefined ? { angle_adjust_deg: p.angle_adjust_deg } : {}),
      ...(p.angle_adjust_at_bend !== undefined ? { angle_adjust_at_bend: p.angle_adjust_at_bend } : {}),
    })),
    ...(src.properties !== undefined ? { properties: { ...src.properties } } : {}),
    ...(src.appearance_override !== undefined ? { appearance_override: { ...src.appearance_override } } : {}),
  };
}

/**
 * Extrae los UIDs de componentes involucrados en una selección,
 * independientemente de su tipo. Devuelve array vacío para null.
 */
export function selectedComponentUids(sel: Selection): Uid[] {
  if (!sel) return [];
  if (sel.kind === 'multi') return sel.component_uids;
  if (sel.kind === 'component' || sel.kind === 'pin' || sel.kind === 'bend') {
    return [sel.component_uid];
  }
  return [];
}

/**
 * UIDs de CABLES incluidos en la selección (single wire, wire-bend,
 * o multi con wires). Vacío si no hay cables involucrados.
 */
export function selectedWireUids(sel: Selection): Uid[] {
  if (!sel) return [];
  if (sel.kind === 'wire' || sel.kind === 'wire-bend') return [sel.wire_uid];
  if (sel.kind === 'multi') return sel.wire_uids ?? [];
  return [];
}

/** Igualdad de dos listas de UIDs tratadas como conjuntos. */
function sameUidSet(a: readonly Uid[], b: readonly Uid[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((u) => set.has(u));
}

/** Bounding box axis-aligned de un cable (a partir de su path). */
function wireBoundingBox(wire: Wire): { x: number; y: number; width: number; height: number } {
  const wp = wireAsPin(wire);
  const path = computePinPath3D(wp.def, wp.state);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of path) {
    if (v.position.x < minX) minX = v.position.x;
    if (v.position.y < minY) minY = v.position.y;
    if (v.position.x > maxX) maxX = v.position.x;
    if (v.position.y > maxY) maxY = v.position.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function selectionsEqual(a: Selection, b: Selection): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'multi' && b.kind === 'multi') {
    if (a.component_uids.length !== b.component_uids.length) return false;
    for (let i = 0; i < a.component_uids.length; i++) {
      if (a.component_uids[i] !== b.component_uids[i]) return false;
    }
    const aw = a.wire_uids ?? [];
    const bw = b.wire_uids ?? [];
    if (aw.length !== bw.length) return false;
    for (let i = 0; i < aw.length; i++) if (aw[i] !== bw[i]) return false;
    return true;
  }
  if (a.kind === 'multi' || b.kind === 'multi') return false;
  if (a.kind === 'wire' && b.kind === 'wire') return a.wire_uid === b.wire_uid;
  if (a.kind === 'wire' || b.kind === 'wire') return false;
  if (a.kind === 'wire-bend' && b.kind === 'wire-bend') {
    // `side` forma parte de la identidad de la selección: cambiarlo
    // repinta el overlay (resalta la otra mitad del cable) y cambia
    // qué lado se moverá al doblar.
    return a.wire_uid === b.wire_uid && a.bend_index === b.bend_index && a.side === b.side;
  }
  if (a.kind === 'wire-bend' || b.kind === 'wire-bend') return false;
  if (a.kind === 'solder' && b.kind === 'solder') return a.solder_uid === b.solder_uid;
  if (a.kind === 'solder' || b.kind === 'solder') return false;
  if (a.component_uid !== b.component_uid) return false;
  if (a.kind === 'component') return true;
  if (a.kind === 'pin') return b.kind === 'pin' && a.pin_id === b.pin_id;
  if (a.kind === 'bend' && b.kind === 'bend') {
    return a.pin_id === b.pin_id && a.bend_index === b.bend_index;
  }
  return false;
}
